/**
 * ScopeColumns — Pure-canvas Finder-style column browser.
 *
 * The entire grid (headers, dimension pickers, entry lists with sparklines)
 * is drawn on a single <canvas>. Zero DOM nodes for content means zero
 * reflow during sidebar resize.
 */

import { memo, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, query, useCurrentRange } from "../lib/dataClient.js";
import { DIMENSION_LIST, DIMENSIONS, canMultiSelect, encodeScopePath, scopeStepLabel, stepIds, useLabels, dimensionSentenceLabel } from "../lib/dimensionDefs.js";
import { CACHE_NOTIFY, useCachedState } from "./SidebarNavRuntime.jsx";
import Select from "./Select.jsx";
import { useThemeVersion } from "../lib/theme.js";
import { analyticsErrorCopy } from "../lib/analyticsClient.js";
import { useMyPermissions } from "../lib/permissions.js";

// ── Layout constants ─────────────────────────────────────────────

const DEFAULT_COL_W = 200;
const MIN_COL_W = 120;
const HDR_H = 28;
const PICKER_H = 36;
const ENTRY_H = 46;
const PX = 10; // horizontal padding
const RESIZE_HANDLE_W = 6; // pixels on each side of column border
const MINIMAP_W = 10; // width of per-column minimap strip (entries columns only)
const MINIMAP_EDGE_RESERVE = 3; // reserve the outer px for the resize handle

// Window tuning for the flow-links viewport query.
//   Buffer = one full viewport of rows on each side of the visible
//     window, so small scrolls always have data ready and a flow line
//     whose anchor is just off-screen still draws the visible portion
//     of its curve. Total fetched window ≈ 3× visible (one screen
//     above, visible, one screen below).
//   FLOW_CHUNK_ROWS — alignment quantum. The fetched window's first/
//     last rows snap to multiples of this so a 1-3 row scroll within a
//     chunk doesn't change the request signature → no refetch, no loader
//     flash. Crossing a chunk boundary triggers a fresh fetch.
//   FLOW_VISIBLE_LIMIT — hard cap on ids per side per pair. Keeps the
//     request manageable even on very tall canvases (e.g., 30+ visible
//     rows × 3 = 90+ would overshoot, gets clipped).
const FLOW_CHUNK_ROWS = 4;
const FLOW_VISIBLE_LIMIT = 128;

// Per-entries-column pagination. The first request gets COLUMN_PAGE_SIZE
// rows; when the viewport scrolls to within COLUMN_PREFETCH_ROWS of the
// loaded end, we bump that column's extension counter and the next
// columns query sends a larger limit (offset stays 0 — pages
// accumulate by growing limit, not by appending). Server caps via
// COLUMNS_ENTRY_LIMIT default (200) but accepts whatever's sent.
const COLUMN_PAGE_SIZE = 200;
const COLUMN_PREFETCH_ROWS = 30;

// Estimate how many top rows buildColumnsWithFlowLinks should seed flow links
// for, given a canvas of height `h`: the visible row count plus one viewport
// of buffer (matching computeEntriesWindows' scroll=0 window) plus a chunk of
// margin, then quantized to 32-row tiers so minor resizes don't re-key the
// columns query. No ceiling — the seed tracks the actual viewport (which is
// physically bounded by screen height anyway); a short finder seeds ~32–64
// rows, a tall one seeds more.
function estimateSeedRows(h) {
  const contentH = Math.max(0, h - HDR_H);
  const visible = Math.max(4, Math.ceil(contentH / ENTRY_H));
  const withBuffer = visible * 2 + FLOW_CHUNK_ROWS;
  return Math.ceil(withBuffer / 32) * 32;
}

// Compute first/last fetched row per entries column from current
// scroll, including a viewport-sized buffer and chunk-alignment.
// Pickers and empty columns return null (no flow-link participation).
function computeEntriesWindows(columns, state, h) {
  const contentTop = HDR_H;
  const contentH = Math.max(0, h - contentTop);
  // Buffer = one full viewport of rows. Floor at 4 so a tiny canvas
  // still gets a usable buffer.
  const visibleRowCount = Math.max(4, Math.floor(contentH / ENTRY_H));
  const bufferRows = visibleRowCount;
  return columns.map((col, ci) => {
    if (col.type !== "entries") return null;
    const items = col.entries || [];
    if (items.length === 0) return null;
    const sy = state.colScrollY?.[ci] || 0;
    const visibleFirst = Math.max(0, Math.floor(sy / ENTRY_H));
    const visibleLast = Math.min(
      items.length - 1,
      Math.floor((sy + contentH) / ENTRY_H),
    );
    // Expand by a full viewport on each side, then snap to chunk
    // boundaries so small scrolls within a chunk leave the fetched id
    // set unchanged.
    const bufferedFirst = Math.max(0, visibleFirst - bufferRows);
    const bufferedLast = Math.min(items.length - 1, visibleLast + bufferRows);
    const firstRow = Math.floor(bufferedFirst / FLOW_CHUNK_ROWS) * FLOW_CHUNK_ROWS;
    const lastRow = Math.min(
      items.length - 1,
      Math.ceil((bufferedLast + 1) / FLOW_CHUNK_ROWS) * FLOW_CHUNK_ROWS - 1,
    );
    const slice = items.slice(firstRow, Math.min(lastRow + 1, firstRow + FLOW_VISIBLE_LIMIT));
    return { ci, firstRow, dimKey: col.dimKey, ids: slice.map((e) => e.id) };
  });
}

// Build adjacent (a, b) entries-pair windows. Matches the worker's
// `break;` after the first match — only links between immediate
// neighbour entries columns get drawn; intervening pickers are skipped.
function buildAdjacentPairs(columns, windows) {
  const pairs = [];
  for (let a = 0; a < columns.length; a++) {
    const wA = windows[a];
    if (!wA || !wA.ids.length) continue;
    for (let b = a + 1; b < columns.length; b++) {
      const wB = windows[b];
      if (!wB || !wB.ids.length) continue;
      pairs.push({
        colA: a, colB: b,
        aDim: wA.dimKey, aIds: wA.ids,
        bDim: wB.dimKey, bIds: wB.ids,
      });
      break;
    }
  }
  return pairs;
}

function buildFlowBridgePairs(columns) {
  const pairs = [];
  for (let a = 0; a < columns.length; a += 1) {
    if (columns[a]?.type !== "entries" || !(columns[a].entries || []).length) continue;
    for (let b = a + 1; b < columns.length; b += 1) {
      if (columns[b]?.type !== "entries" || !(columns[b].entries || []).length) continue;
      if (b > a + 1) pairs.push({ colA: a, colB: b });
      break;
    }
  }
  return pairs;
}

// Zip the flow-links response pairs with the local pair list to
// translate (aId, bId) pairs back to (rowA, rowB) indices for
// drawFlowLinks. Linear `findIndex` is fine — entries cap at 500.
function reshapeFlowLinks(response, localPairs, columns) {
  const out = [];
  const respPairs = response?.pairs || [];
  for (let i = 0; i < localPairs.length; i++) {
    const lp = localPairs[i];
    const rp = respPairs[i];
    if (!rp) continue;
    const aEntries = columns[lp.colA]?.entries || [];
    const bEntries = columns[lp.colB]?.entries || [];
    for (const link of rp.links || []) {
      const rowA = aEntries.findIndex((e) => String(e.id) === String(link.aId));
      const rowB = bEntries.findIndex((e) => String(e.id) === String(link.bId));
      if (rowA >= 0 && rowB >= 0) {
        out.push({ colA: lp.colA, rowA, colB: lp.colB, rowB, count: link.count });
      }
    }
  }
  return out;
}

function getColW(ci, state) {
  return state.colWidths?.[ci] ?? DEFAULT_COL_W;
}

function getColX(ci, state) {
  let x = 0;
  for (let i = 0; i < ci; i++) x += getColW(i, state);
  return x;
}

function getTotalW(colCount, state) {
  let w = 0;
  for (let i = 0; i < colCount; i++) w += getColW(i, state);
  return w;
}

// ── Theme color cache ────────────────────────────────────────────

let _tc = null;
let _tid = null;
function tc() {
  const id = document.documentElement.dataset.theme || "light";
  if (_tc && _tid === id) return _tc;
  const s = getComputedStyle(document.documentElement);
  const g = (v) => s.getPropertyValue(v).trim();
  _tc = {
    bg: g("--bg"), ink: g("--ink"), muted: g("--muted"),
    line: g("--line"), lineSoft: g("--line-soft"),
    greenSoft: g("--green-soft"), green: g("--green"),
    controlBg: g("--control-bg"),
    // Minimap bar color — solid shade per theme so overlapping draws don't
    // accumulate alpha (which makes dense columns look blown-out).
    minimapBar: id === "dark" ? "#3a332c" : "#ddd2bd",
  };
  _tid = id;
  return _tc;
}

// Column descriptors and flow links are computed in the Web Worker
// via buildColumnsWithFlowLinks(). The component receives pre-built data.

import { roundRectPath, drawFlowCurve, hexToRgba } from "../lib/canvasHelpers.js";
import { formatCompact } from "../lib/renderers.js";
import { STIPPLE_SIZE_COMPACT, STIPPLE_DOT_RADIUS_COMPACT } from "../lib/stipple.js";
import { observeSettledSize } from "../lib/settledResize.js";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

function drawFlowLinks(ctx, w, h, cols, state, flowLinks, haloColorOverride, dimmed = false) {
  // Drop links whose column indices no longer exist. Possible during a
  // scope transition where new columns have arrived but the previously
  // fetched flow links haven't yet been replaced — without this guard
  // every downstream `cols[link.colX].type` lookup crashes.
  const liveLinks = flowLinks.filter((link) => cols[link.colA] && cols[link.colB]);
  if (!liveLinks.length) return;
  const c = tc();
  // Focus alpha kicks in when a row is selected or hovered: links
  // touching the active row pop, the rest fall back so the user can read
  // that row's distribution against the sibling space.
  const baseLinkAlpha = 0.22;
  const baseNodeAlpha = 0.44;
  const selectedLinkAlpha = 0.68;
  const selectedNodeAlpha = 0.85;
  // Dim layer is the rest of the bundle as context — kept readable
  // so the user can still see the surrounding shape, not just the
  // highlighted thread. It needs to be quiet enough that a downstream
  // endpoint focus can read as "back to clusters" instead of being
  // overwhelmed by the parent cluster's forward fan-out.
  const dimmedLinkAlpha = 0.08;
  const dimmedNodeAlpha = 0.22;
  const { scrollX, colScrollY } = state;
  const contentTop = HDR_H;
  const NODE_W = 4;
  const NODE_MAX_H = 36;
  const NODE_INSET = 10;

  // Map each entries column -> the SET of selected row indices. Hover is
  // handled below as a higher-priority focus state; selected scope rows are
  // the fallback so active highlights persist while the user drills deeper.
  //
  // Default selected-row priority is per bridge, not global: for links
  // between A -> B, B's active row wins if present; otherwise A's active
  // row drives the highlight. That keeps each selected scope row readable
  // against its parent while still letting it fan forward when no child row
  // has been selected yet.
  const selectedRowsByCol = new Map();
  for (let ci = 0; ci < cols.length; ci += 1) {
    const col = cols[ci];
    if (col.type !== "entries") continue;
    const ids = Array.isArray(col.selectedIds) && col.selectedIds.length
      ? col.selectedIds
      : (col.selectedId != null ? [col.selectedId] : []);
    if (ids.length === 0) continue;
    const set = new Set();
    for (const id of ids) {
      const idx = (col.entries || []).findIndex((e) => String(e.id) === String(id));
      if (idx >= 0) set.add(idx);
    }
    if (set.size > 0) selectedRowsByCol.set(ci, set);
  }

  let hoverFocus = null;
  if (state.hoverCol >= 0 && state.hoverRow >= 0) {
    const hoverCol = cols[state.hoverCol];
    if (hoverCol && hoverCol.type === "entries"
        && (hoverCol.entries || [])[state.hoverRow]) {
      hoverFocus = [state.hoverCol, new Set([state.hoverRow])];
    }
  }

  const linkKey = (link) => `${link.colA}:${link.rowA}->${link.colB}:${link.rowB}`;
  const addRow = (map, colIdx, rowIdx) => {
    let rows = map.get(colIdx);
    if (!rows) {
      rows = new Set();
      map.set(colIdx, rows);
    }
    rows.add(rowIdx);
  };

  // Hover gets a richer, temporary traversal. The hovered row still owns
  // its immediate backward + forward links, then any rows lit on the
  // upstream side continue walking backward through earlier bridges, and
  // any rows lit on the downstream side continue walking forward through
  // later bridges.
  // Resting selected-row focus intentionally does not do this because it
  // can get visually dense when several scope rows are active at once.
  const hoverSelectedLinkKeys = new Set();
  if (hoverFocus) {
    const [hoverCol, hoverRows] = hoverFocus;
    let upstreamFrontier = new Map();
    let downstreamFrontier = new Map();

    for (const link of liveLinks) {
      if (link.colA === hoverCol && hoverRows.has(link.rowA)) {
        hoverSelectedLinkKeys.add(linkKey(link));
        addRow(downstreamFrontier, link.colB, link.rowB);
      }
      if (link.colB === hoverCol && hoverRows.has(link.rowB)) {
        hoverSelectedLinkKeys.add(linkKey(link));
        addRow(upstreamFrontier, link.colA, link.rowA);
      }
    }

    for (let hop = 0; hop < cols.length && upstreamFrontier.size > 0; hop += 1) {
      const nextFrontier = new Map();
      for (const link of liveLinks) {
        const rows = upstreamFrontier.get(link.colB);
        if (!rows || !rows.has(link.rowB)) continue;
        hoverSelectedLinkKeys.add(linkKey(link));
        addRow(nextFrontier, link.colA, link.rowA);
      }
      upstreamFrontier = nextFrontier;
    }

    for (let hop = 0; hop < cols.length && downstreamFrontier.size > 0; hop += 1) {
      const nextFrontier = new Map();
      for (const link of liveLinks) {
        const rows = downstreamFrontier.get(link.colA);
        if (!rows || !rows.has(link.rowA)) continue;
        hoverSelectedLinkKeys.add(linkKey(link));
        addRow(nextFrontier, link.colB, link.rowB);
      }
      downstreamFrontier = nextFrontier;
    }
  }

  const hasFocus = hoverFocus ? hoverSelectedLinkKeys.size > 0 : selectedRowsByCol.size > 0;
  const isLinkSelected = (link) => {
    if (hoverFocus) {
      return hoverSelectedLinkKeys.has(linkKey(link));
    }

    const right = selectedRowsByCol.get(link.colB);
    if (right && right.size > 0) return right.has(link.rowB);

    const left = selectedRowsByCol.get(link.colA);
    if (left && left.size > 0) return left.has(link.rowA);

    return false;
  };
  const isNodeHighlighted = (colIdx, rowIdx) => {
    if (hoverFocus) {
      const [hoverCol, hoverRows] = hoverFocus;
      return colIdx === hoverCol && hoverRows.has(rowIdx);
    }
    const rows = selectedRowsByCol.get(colIdx);
    return !!(rows && rows.has(rowIdx));
  };

  // Aggregate totals per source/target row
  const srcTotals = new Map();
  const tgtTotals = new Map();
  let globalMax = 0;
  for (const link of liveLinks) {
    const sk = `${link.colA}:${link.rowA}`;
    const tk = `${link.colB}:${link.rowB}`;
    srcTotals.set(sk, (srcTotals.get(sk) || 0) + link.count);
    tgtTotals.set(tk, (tgtTotals.get(tk) || 0) + link.count);
    globalMax = Math.max(globalMax, srcTotals.get(sk), tgtTotals.get(tk));
  }
  if (!globalMax) return;

  function nodeH(total) {
    return Math.max(6, (total / globalMax) * NODE_MAX_H);
  }

  const srcOffsets = new Map();
  const tgtOffsets = new Map();
  const drawnNodes = new Set();

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, contentTop, w, h - contentTop);
  ctx.clip();

  // Compute node positions
  for (const link of liveLinks) {
    const colA = cols[link.colA];
    const colB = cols[link.colB];
    const colAW = getColW(link.colA, state);
    const itemHA = colA.type === "picker" ? PICKER_H : ENTRY_H;
    const itemHB = colB.type === "picker" ? PICKER_H : ENTRY_H;
    const syA = colScrollY[link.colA] || 0;
    const syB = colScrollY[link.colB] || 0;

    const sk = `${link.colA}:${link.rowA}`;
    if (!srcOffsets.has(sk)) {
      const nh = nodeH(srcTotals.get(sk));
      const nx = getColX(link.colA, state) + colAW + NODE_INSET - scrollX;
      const ny = contentTop + link.rowA * itemHA + itemHA / 2 - nh / 2 - syA;
      srcOffsets.set(sk, { x: nx, y: ny, consumed: 0, total: nh });
    }

    const tk = `${link.colB}:${link.rowB}`;
    if (!tgtOffsets.has(tk)) {
      const nh = nodeH(tgtTotals.get(tk));
      const nx = getColX(link.colB, state) - NODE_INSET - NODE_W - scrollX;
      const ny = contentTop + link.rowB * itemHB + itemHB / 2 - nh / 2 - syB;
      tgtOffsets.set(tk, { x: nx, y: ny, consumed: 0, total: nh });
    }
  }

  // Pass 1: draw link curves (stroked, like the Sankey). `dimmed` swaps the
  // green for a neutral grey so the bundle recedes when the flow column is
  // hovered/dragged — a tone swap, not a canvas filter, so it's cheap per frame.
  const tone = dimmed ? c.muted : c.green;
  const styles = haloColorOverride ? getComputedStyle(document.documentElement) : null;
  const bg = haloColorOverride
    ? (haloColorOverride.startsWith("var(")
      ? styles.getPropertyValue(haloColorOverride.slice(4, -1)).trim()
      : haloColorOverride)
    : c.bg;

  // First positions pass: walk the links once to compute (x, y) for
  // every link's source/target slice. We need these up-front so the
  // ordered draw passes below can render without re-running the
  // consume bookkeeping.
  const linkGeometry = [];
  for (const link of liveLinks) {
    const sk = `${link.colA}:${link.rowA}`;
    const tk = `${link.colB}:${link.rowB}`;
    const src = srcOffsets.get(sk);
    const tgt = tgtOffsets.get(tk);
    if (!src || !tgt) continue;

    const srcSlice = (link.count / srcTotals.get(sk)) * src.total;
    const tgtSlice = (link.count / tgtTotals.get(tk)) * tgt.total;

    const x0 = src.x + NODE_W;
    const y0 = src.y + src.consumed + srcSlice / 2;
    src.consumed += srcSlice;

    const x1 = tgt.x;
    const y1 = tgt.y + tgt.consumed + tgtSlice / 2;
    tgt.consumed += tgtSlice;

    const norm = link.count / globalMax;
    const strokeWidth = Math.max(0.75, Math.min(2.5, 0.75 + Math.pow(norm, 0.66) * 1.5));
    const haloWidth = strokeWidth + 2.4;
    const cpx = (x0 + x1) / 2;

    linkGeometry.push({
      link, x0, y0, x1, y1, cpx, strokeWidth, haloWidth,
      selected: hasFocus && isLinkSelected(link),
    });
  }

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Each pass interleaves halo + stroke per link so each link's halo
  // carves into earlier links it crosses — the original Sankey
  // "topography at crossings" effect that made the bundle look dense.
  // Drawing all halos in one batch and all strokes in another would
  // flatten that and the lines read as too thin.
  const drawLinkPair = (g, alpha) => {
    ctx.strokeStyle = bg;
    ctx.lineWidth = g.haloWidth;
    drawFlowCurve(ctx, g.x0, g.y0, g.x1, g.y1, g.cpx, g.cpx);
    ctx.stroke();
    ctx.strokeStyle = hexToRgba(tone, alpha);
    ctx.lineWidth = g.strokeWidth;
    drawFlowCurve(ctx, g.x0, g.y0, g.x1, g.y1, g.cpx, g.cpx);
    ctx.stroke();
  };

  if (!hasFocus) {
    // No row focus: every link gets the base alpha, interleaved
    // halo+stroke just like the original.
    for (const g of linkGeometry) drawLinkPair(g, baseLinkAlpha);
  } else {
    // Row focus active: dim layer first (so focused halos can still
    // carve through it), then focused layer on top at full strength.
    for (const g of linkGeometry) {
      if (g.selected) continue;
      drawLinkPair(g, dimmedLinkAlpha);
    }
    for (const g of linkGeometry) {
      if (!g.selected) continue;
      drawLinkPair(g, selectedLinkAlpha);
    }
  }

  // A node is "lit" when at least one focused link terminates on it.
  // Source-row nodes get lit when they're feeding into the focused
  // target; target-row nodes get lit when they're receiving from the
  // focused source. Focused nodes themselves are also lit. Lit but
  // not-selected nodes get a subtle ring outline so the bright link
  // doesn't visually trail off into a faded bar.
  const litNodes = new Set();
  if (hasFocus) {
    for (const g of linkGeometry) {
      if (!g.selected) continue;
      litNodes.add(`${g.link.colA}:${g.link.rowA}`);
      litNodes.add(`${g.link.colB}:${g.link.rowB}`);
    }
  }

  const drawNode = (key, pos) => {
    const [colIdx, rowIdx] = key.split(":").map(Number);
    const highlighted = isNodeHighlighted(colIdx, rowIdx);
    const lit = litNodes.has(key);
    const alpha = hasFocus
      ? (highlighted ? selectedNodeAlpha : (lit ? baseNodeAlpha : dimmedNodeAlpha))
      : baseNodeAlpha;
    ctx.fillStyle = hexToRgba(tone, alpha);
    roundRectPath(ctx, pos.x, pos.y, NODE_W, pos.total, 2);
    ctx.fill();
    // Ring outline on lit-but-not-highlighted nodes — gives the bright
    // line something solid to terminate against. The highlighted node
    // (selected or hovered) is already at peak alpha and doesn't need it.
    if (hasFocus && lit && !highlighted) {
      ctx.save();
      ctx.strokeStyle = hexToRgba(tone, 0.7);
      ctx.lineWidth = 1;
      const inset = 0.5;
      roundRectPath(
        ctx,
        pos.x - inset, pos.y - inset,
        NODE_W + inset * 2, pos.total + inset * 2,
        2.5,
      );
      ctx.stroke();
      ctx.restore();
    }
  };

  for (const [key, pos] of srcOffsets) drawNode(key, pos);
  for (const [key, pos] of tgtOffsets) drawNode(key, pos);

  ctx.restore();
}

function drawFlowLoadingBridges(ctx, w, h, cols, state, bridgePairs) {
  if (!bridgePairs.length) return;
  const c = tc();
  const { scrollX } = state;
  const contentTop = HDR_H;
  const tone = c.green;

  ctx.save();
  for (const pair of bridgePairs) {
    const leftW = getColW(pair.colA, state);
    const x0 = getColX(pair.colA, state) + leftW - scrollX;
    const x1 = getColX(pair.colB, state) - scrollX;
    const bw = x1 - x0;
    if (bw <= 10 || x1 < 0 || x0 > w) continue;

    // Replace the intervening picker header with an explicit loading cue,
    // but avoid drawing fake flow geometry; that reads as data.
    ctx.fillStyle = c.bg;
    ctx.fillRect(x0, 0, bw, HDR_H);
    ctx.font = "500 10px Inter,system-ui,sans-serif";
    ctx.letterSpacing = "0.12em";
    ctx.fillStyle = c.muted;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("LOADING LINKS", x0 + PX, HDR_H / 2 + 1);
    ctx.letterSpacing = "0px";
    ctx.fillStyle = c.line;
    ctx.fillRect(x0, HDR_H - 0.5, bw, 0.5);

    ctx.fillStyle = hexToRgba(tone, 0.025);
    ctx.fillRect(x0, contentTop, bw, h - contentTop);

    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, contentTop, bw, h - contentTop);
    ctx.clip();

    const cx = x0 + bw / 2;
    const cy = contentTop + Math.min(64, Math.max(30, (h - contentTop) * 0.12));
    ctx.fillStyle = hexToRgba(tone, 0.28);
    for (let i = -1; i <= 1; i += 1) {
      ctx.beginPath();
      ctx.arc(cx + i * 8, cy, i === 0 ? 2 : 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  ctx.restore();
}

// A bridge position (picker between two entries columns) where the fetch
// settled with no co-occurrence to draw. Instead of leaking the dimension
// picker through — which reads as "pick something here" — blank the span and
// say so, matching the loading/linked states' "this column hosts flow" model.
// skipMsgCol: a bridge whose intervening picker is being hovered still gets
// blanked, but its "(no flow available)" text is suppressed — the pop-out
// label drawn on top replaces it, so the two don't collide.
function drawNoFlowBridges(ctx, w, h, cols, state, bridgePairs, skipMsgCol = -1) {
  if (!bridgePairs.length) return;
  const c = tc();
  const { scrollX } = state;
  const contentTop = HDR_H;
  ctx.save();
  for (const pair of bridgePairs) {
    const leftW = getColW(pair.colA, state);
    const x0 = getColX(pair.colA, state) + leftW - scrollX;
    const x1 = getColX(pair.colB, state) - scrollX;
    const bw = x1 - x0;
    if (bw <= 10 || x1 < 0 || x0 > w) continue;
    ctx.fillStyle = c.bg;
    ctx.fillRect(x0, 0, bw, h);
    ctx.fillStyle = c.line;
    ctx.fillRect(x0, HDR_H - 0.5, bw, 0.5);
    if (skipMsgCol > pair.colA && skipMsgCol < pair.colB) continue;
    ctx.font = "400 12px Inter,system-ui,sans-serif";
    ctx.fillStyle = c.muted;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("(no flow available)", x0 + bw / 2, contentTop + (h - contentTop) / 2);
    ctx.textAlign = "left";
  }
  ctx.restore();
}

// Hover affordance over a hidden flow column. The column can't be clicked as a
// picker (flow links / "no flow" fill it), so on hover surface a label that
// pops the trailing selection and brings the dimension picker back.
function drawPopOutAffordance(ctx, w, h, ci, state, cols) {
  const c = tc();
  const colW = getColW(ci, state);
  const x = getColX(ci, state) - state.scrollX;
  if (x + colW < 0 || x > w) return;
  const contentTop = HDR_H;
  // Line the label up with the selected row of the nearest entries column to
  // the left — the origin of the flow fan — so it sits where the bundle starts.
  // Fall back to vertical center when there's no resolvable/visible selection.
  let cy = contentTop + (h - contentTop) / 2;
  let leftCol = -1;
  for (let k = ci - 1; k >= 0; k -= 1) {
    if (cols?.[k]?.type === "entries") { leftCol = k; break; }
  }
  if (leftCol >= 0) {
    const col = cols[leftCol];
    const ids = (Array.isArray(col.selectedIds) && col.selectedIds.length)
      ? col.selectedIds
      : (col.selectedId != null ? [col.selectedId] : []);
    if (ids.length) {
      const rowIdx = (col.entries || []).findIndex((e) => String(e.id) === String(ids[0]));
      if (rowIdx >= 0) {
        const rowCy = contentTop + rowIdx * ENTRY_H + ENTRY_H / 2 - (state.colScrollY?.[leftCol] || 0);
        if (rowCy >= contentTop + 12 && rowCy <= h - 12) cy = rowCy;
      }
    }
  }
  // Two behaviours, two labels. When a downstream row is committed to the
  // scope, the pop-out just unselects it (keeping the dimension) — a light
  // "← BACK". With nothing selected the column IS the dimension picker, so
  // "← PICK DIMENSION" stands (re-choosing the dimension).
  let rightCol = -1;
  for (let k = ci + 1; k < cols.length; k += 1) {
    if (cols?.[k]?.type === "entries") { rightCol = k; break; }
  }
  const rc = rightCol >= 0 ? cols[rightCol] : null;
  const downstreamSelected = !!rc
    && ((Array.isArray(rc.selectedIds) && rc.selectedIds.length > 0) || rc.selectedId != null);
  const label = downstreamSelected ? "← BACK" : "← PICK DIMENSION";
  // Plain finder-style label — no box — matching the column-state text
  // ("LOADING LINKS", "(no flow available)"). The greyed-out lines + pointer
  // cursor carry the "interactive" read without a foreign chip.
  ctx.save();
  ctx.font = "500 11px Inter,system-ui,sans-serif";
  ctx.letterSpacing = "0.1em";
  ctx.fillStyle = c.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + colW / 2, cy);
  ctx.letterSpacing = "0px";
  ctx.restore();
}

// ── Drawing ──────────────────────────────────────────────────────

// Glimmer sweep across the just-clicked row while the next column loads.
// A soft band (transparent → tone → transparent) sweeping L→R across ~1.4s,
// matching the lazy-sparkline shimmer's language — a glint over the row, not a
// colored block. Shared by the dimension picker and the entries list so both
// nav idioms read identically.
function drawRowGlimmer(ctx, x, iy, colW, itemH, c) {
  const bandW = Math.max(18, colW * 0.22);
  const phase = (performance.now() / 1400) % 1;
  const bandX = x - bandW + (colW + bandW) * phase;
  const grad = ctx.createLinearGradient(bandX, 0, bandX + bandW, 0);
  grad.addColorStop(0,   "rgba(0,0,0,0)");
  grad.addColorStop(0.5, c.ink);
  grad.addColorStop(1,   "rgba(0,0,0,0)");
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, iy, colW, itemH);
  ctx.clip();
  ctx.globalAlpha = 0.1;
  ctx.fillStyle = grad;
  ctx.fillRect(bandX, iy, bandW, itemH);
  ctx.restore();
}

function drawGrid(ctx, w, h, cols, state, hiddenPickerCols = null, extraScrollWidth = 0, pendingReplacementIndex = -1, lazyCtx = null) {
  // lazyCtx is the closure-bound { lazySparklineMap, deferredScopePath, range }
  // needed for the lazy-sparkline overlay lookup. Optional so legacy
  // callers / non-mcp paths can omit it without exploding.
  const lazySparklineMap = lazyCtx?.lazySparklineMap ?? null;
  const deferredScopePath = lazyCtx?.deferredScopePath ?? [];
  const range = lazyCtx?.range ?? null;
  // Picker column index whose list is being rebuilt by an in-flight nav:
  // glimmer the just-clicked row, dim the rest. -1 when nothing is loading.
  // loadingPickerSelId is the clicked dim's id (matched by id, since the
  // drawn rows are stale and their item.selected is the prior selection).
  const loadingPickerIdx = lazyCtx?.loadingPickerIdx ?? -1;
  const loadingPickerSelId = lazyCtx?.loadingPickerSelId ?? null;
  // Same idea for an entries-list nav (value click): glimmer the clicked row.
  // Distinct from the picker because the entries list is *not* rebuilt — its
  // rows stay, so the unchosen rows are left bright (only a new column loads).
  const loadingEntryIdx = lazyCtx?.loadingEntryIdx ?? -1;
  const loadingEntrySelId = lazyCtx?.loadingEntrySelId ?? null;
  const c = tc();
  ctx.clearRect(0, 0, w, h);

  // Clamp horizontal scroll to valid range
  const maxScrollX = Math.max(0, getTotalW(cols.length, state) + extraScrollWidth - w);
  state.scrollX = Math.max(0, Math.min(maxScrollX, state.scrollX));

  const { scrollX, colScrollY, hoverCol, hoverRow } = state;

  // Full-width header background so there's no gap to the right of the last column
  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, w, HDR_H);
  ctx.fillStyle = c.line;
  ctx.fillRect(0, HDR_H - 0.5, w, 0.5);

  for (let ci = 0; ci < cols.length; ci++) {
    const col = cols[ci];
    const colW = getColW(ci, state);
    const x = getColX(ci, state) - scrollX;
    if (x + colW < 0 || x > w) continue;

    // Clip column
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.max(0, x), 0, colW - Math.max(0, -x), h);
    ctx.clip();

    // Header background
    ctx.fillStyle = c.bg;
    ctx.fillRect(x, 0, colW, HDR_H);
    // Entries columns paint their header (label + search + sort) via the
    // DOM overlay so we only draw the label here for picker columns.
    if (col.type !== "entries") {
      ctx.font = "500 10px Inter,system-ui,sans-serif";
      ctx.letterSpacing = "0.12em";
      ctx.fillStyle = c.muted;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(col.label.toUpperCase(), x + PX, HDR_H / 2 + 1);
      ctx.letterSpacing = "0px";
    }
    // Header border
    ctx.fillStyle = c.line;
    ctx.fillRect(x, HDR_H - 0.5, colW, 0.5);

    if (ci === pendingReplacementIndex) {
      ctx.fillStyle = c.bg;
      ctx.fillRect(x, HDR_H, colW, h - HDR_H);
      ctx.fillStyle = c.line;
      ctx.fillRect(x + colW - 0.5, 0, 0.5, h);
      ctx.restore();
      continue;
    }

    // Skip content for picker columns that have flow links passing through
    if (hiddenPickerCols?.has(ci) && col.type === "picker") {
      ctx.fillStyle = c.line;
      ctx.fillRect(x + colW - 0.5, 0, 0.5, h);
      ctx.restore();
      continue;
    }

    // Entries column whose rows haven't loaded yet — paint a focused
    // skeleton in just this column instead of leaving it blank.
    if (col.type === "entries" && col.pending) {
      const skRowH = ENTRY_H;
      const skRows = Math.max(1, Math.floor((h - HDR_H) / skRowH));
      for (let ri = 0; ri < skRows; ri++) {
        const ry = HDR_H + ri * skRowH;
        const alpha = Math.max(0.14, 0.7 - ri * 0.05);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = c.lineSoft;
        ctx.fillRect(x + PX, ry + 10, colW * (0.45 + ((ri * 17) % 25) / 100), 10);
        ctx.fillRect(x + PX, ry + 26, colW * (0.55 + ((ri * 13) % 20) / 100), 10);
        ctx.globalAlpha = 1;
        ctx.fillStyle = c.lineSoft;
        ctx.fillRect(x, ry + skRowH - 0.5, colW, 0.5);
      }
      ctx.fillStyle = c.line;
      ctx.fillRect(x + colW - 0.5, 0, 0.5, h);
      ctx.restore();
      continue;
    }

    // Items
    const isPicker = col.type === "picker";
    const isLoadingPicker = isPicker && ci === loadingPickerIdx;
    const itemH = isPicker ? PICKER_H : ENTRY_H;
    const items = isPicker ? col.items : col.entries;
    const contentTop = HDR_H;
    const maxSy = Math.max(0, items.length * itemH - (h - contentTop));
    if ((colScrollY[ci] || 0) > maxSy) colScrollY[ci] = maxSy;
    const sy = colScrollY[ci] || 0;

    // Clip content area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, contentTop, colW, h - contentTop);
    ctx.clip();

    for (let ri = 0; ri < items.length; ri++) {
      const item = items[ri];
      const iy = contentTop + ri * itemH - sy;
      if (iy + itemH < contentTop || iy > h) continue;

      const isHov = ci === hoverCol && ri === hoverRow;
      // Multi-select aware: any id in selectedIds counts as selected so
      // every chosen row gets the active row treatment (left bar + soft
      // bg). Falls back to the singular selectedId for back-compat.
      const isSel = isPicker
        ? item.selected
        : (Array.isArray(col.selectedIds) && col.selectedIds.length
            ? col.selectedIds.map(String).includes(String(item.id))
            : String(item.id) === String(col.selectedId));

      // The just-clicked row whose next column is loading — glimmers in both
      // the dimension picker and the entries list so the two navs read alike.
      // Matched by id because the drawn rows are stale (their selected state
      // still reflects the prior selection).
      const isGlimmerRow = isPicker
        ? (isLoadingPicker && loadingPickerSelId != null && String(item.id) === String(loadingPickerSelId))
        : (ci === loadingEntryIdx && loadingEntrySelId != null && String(item.id) === String(loadingEntrySelId));

      // Background
      if (isSel) {
        ctx.fillStyle = c.greenSoft;
        ctx.fillRect(x, iy, colW, itemH);
        ctx.fillStyle = c.green;
        ctx.fillRect(x, iy, 2, itemH);
      } else if (isHov) {
        ctx.fillStyle = c.controlBg;
        ctx.fillRect(x, iy, colW, itemH);
      }

      if (isPicker) {
        // Dimension picker row. While this picker is being rebuilt, dim the
        // unchosen rows so the eye lands on the just-clicked one (which
        // glimmers at row end), making it read as "this whole list is updating".
        if (isLoadingPicker && !isGlimmerRow) ctx.globalAlpha = 0.32;
        ctx.font = "500 11px Inter,system-ui,sans-serif";
        ctx.letterSpacing = "0.1em";
        ctx.fillStyle = c.ink;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(item.label.toUpperCase(), x + PX, iy + itemH / 2);
        ctx.letterSpacing = "0px";

        ctx.font = "400 11px Inter,system-ui,sans-serif";
        ctx.fillStyle = c.muted;
        ctx.textAlign = "right";
        ctx.fillText(String(item.count), x + colW - (isSel ? 22 : 10), iy + itemH / 2);
        ctx.textAlign = "left";
        ctx.globalAlpha = 1;
      } else {
        // Entry row — label. Reserve `MINIMAP_W` at the right edge for the
        // per-column minimap strip.
        ctx.font = "500 12px Inter,system-ui,sans-serif";
        ctx.fillStyle = c.ink;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        const maxLW = colW - PX - 50 - MINIMAP_W;
        let label = item.label;
        if (ctx.measureText(label).width > maxLW) {
          while (label.length > 4 && ctx.measureText(label + "\u2026").width > maxLW) label = label.slice(0, -1);
          label += "\u2026";
        }
        ctx.fillText(label, x + PX, iy + 8);

        // Activity count
        ctx.font = "400 11px Inter,system-ui,sans-serif";
        ctx.fillStyle = c.muted;
        ctx.textAlign = "right";
        const activityNum = Number(item.activity) || 0;
        const activityLabel = activityNum >= 1000 ? formatCompact(activityNum) : String(item.activity);
        ctx.fillText(activityLabel, x + colW - 14 - MINIMAP_W, iy + 10);
        ctx.textAlign = "left";

        // Sparkline / share bar
        const spW = colW - PX - 14 - MINIMAP_W;
        const spY = iy + 24;
        const spH = 14;
        let series = item.sparkline?.series;
        // Lazy-fetched entries arrive with series === null; consult
        // the overlay map (filled by the viewport effect above) and
        // substitute the real series once it lands.
        const isPendingLazy = series === null;
        if (isPendingLazy && lazySparklineMap) {
          const parentScopePath = deferredScopePath.slice(0, col.depth || 0);
          const lazyKey = `${col.dimKey}|${item.id}|${encodeScopePath(parentScopePath)}|${range}`;
          const overlay = lazySparklineMap.get(lazyKey);
          if (Array.isArray(overlay)) series = overlay;
        }
        if (series?.length > 1) {
          const smax = Math.max(...series) || 1;
          const pad = 2;
          const usableW = spW - pad * 2;
          const usableH = spH - pad * 2;

          // Build points
          const pts = [];
          for (let j = 0; j < series.length; j++) {
            pts.push({
              x: x + PX + pad + (j / (series.length - 1)) * usableW,
              y: spY + pad + usableH - (series[j] / smax) * usableH,
            });
          }

          // Smooth curve path (quadratic bezier)
          const curvePath = new Path2D();
          curvePath.moveTo(pts[0].x, pts[0].y);
          for (let j = 1; j < pts.length; j++) {
            const cx = (pts[j - 1].x + pts[j].x) / 2;
            curvePath.quadraticCurveTo(cx, pts[j - 1].y, pts[j].x, pts[j].y);
          }

          // Share background
          if (item.share > 0) {
            ctx.fillStyle = c.greenSoft;
            ctx.fillRect(x + PX, spY, spW * Math.min(item.share, 100) / 100, spH);
          }

          // Dot-grid fill under curve
          const baseY = spY + spH - pad;
          const areaPath = new Path2D();
          areaPath.addPath(curvePath);
          areaPath.lineTo(pts[pts.length - 1].x, baseY);
          areaPath.lineTo(pts[0].x, baseY);
          areaPath.closePath();
          ctx.save();
          ctx.clip(areaPath);
          ctx.fillStyle = c.green;
          ctx.globalAlpha = 0.55;
          // Shared compact stipple preset (lib/stipple.js) so this hand-rolled
          // loop tracks the same density as the SurfaceChart-driven sparklines
          // in the sidebar tree (EntrySparkline).
          const dotSize = STIPPLE_SIZE_COMPACT;
          const dotR = STIPPLE_DOT_RADIUS_COMPACT;
          for (let dy = spY; dy < spY + spH + dotSize; dy += dotSize) {
            for (let dx = x + PX; dx < x + PX + spW + dotSize; dx += dotSize) {
              ctx.beginPath();
              ctx.arc(dx + 2, dy + 2, dotR, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          ctx.restore();

          // Halo stroke (background-colored outline)
          ctx.save();
          ctx.strokeStyle = c.bg;
          ctx.lineWidth = 3.8;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.stroke(curvePath);
          ctx.restore();

          // Main stroke
          ctx.save();
          ctx.strokeStyle = c.green;
          ctx.lineWidth = 1.4;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.stroke(curvePath);
          ctx.restore();
        } else if (isPendingLazy) {
          // Lazy fetch still in flight — reserve the sparkline
          // envelope (matches loaded state): 0.18 share tint at full
          // sparkline height, faint static baseline at mid-line, then
          // a soft highlight band sweeping L→R across the baseline.
          // The RAF loop in ScopeColumns drives repaints while any
          // pending sparklines remain visible (hasPendingSparklines).
          if (item.share > 0) {
            ctx.save();
            ctx.globalAlpha = 0.18;
            ctx.fillStyle = c.green;
            ctx.fillRect(x + PX, spY, spW * Math.min(item.share, 100) / 100, spH);
            ctx.restore();
          }
          const baselineY = spY + Math.floor(spH / 2);
          const trackLeft = x + PX + 2;
          const trackW = Math.max(0, spW - 4);
          ctx.save();
          ctx.globalAlpha = 0.28;
          ctx.fillStyle = c.muted;
          ctx.fillRect(trackLeft, baselineY, trackW, 1);
          ctx.restore();
          // Sweep: a 25%-wide bright band whose centre travels from
          // trackLeft - bandW/2 to trackRight + bandW/2 across 1.4s.
          if (trackW > 6) {
            const bandW = Math.max(18, trackW * 0.22);
            const phase = (performance.now() / 1400) % 1;
            const bandX = trackLeft - bandW + (trackW + bandW) * phase;
            const grad = ctx.createLinearGradient(bandX, 0, bandX + bandW, 0);
            grad.addColorStop(0,   "rgba(0,0,0,0)");
            grad.addColorStop(0.5, c.muted);
            grad.addColorStop(1,   "rgba(0,0,0,0)");
            ctx.save();
            // Clip so the gradient never paints outside the row's
            // track — without it the soft tails leak under the next
            // column's content.
            ctx.beginPath();
            ctx.rect(trackLeft, baselineY - 1, trackW, 3);
            ctx.clip();
            ctx.globalAlpha = 0.55;
            ctx.fillStyle = grad;
            ctx.fillRect(bandX, baselineY - 1, bandW, 3);
            ctx.restore();
          }
        } else if (item.share > 0) {
          ctx.globalAlpha = 0.4;
          ctx.fillStyle = c.green;
          ctx.fillRect(x + PX, spY + 5, spW * Math.min(item.share, 100) / 100, 3);
          ctx.globalAlpha = 1;
        }
      }

      // Chevron for selected items. Pickers don't reserve minimap space;
      // entries do, so the chevron shifts left when we're in an entries column.
      if (isSel) {
        const ay = isPicker ? iy + itemH / 2 : iy + 20;
        const chevronX = x + colW - 14 - (isPicker ? 0 : MINIMAP_W);
        ctx.beginPath();
        ctx.moveTo(chevronX, ay - 5);
        ctx.lineTo(chevronX + 5, ay);
        ctx.lineTo(chevronX, ay + 5);
        ctx.strokeStyle = c.muted;
        ctx.lineWidth = 1.3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
      }

      // Glimmer sweep over the just-clicked row while its next column loads.
      if (isGlimmerRow) drawRowGlimmer(ctx, x, iy, colW, itemH, c);

      // Row border
      ctx.fillStyle = c.lineSoft;
      ctx.fillRect(x, iy + itemH - 0.5, colW, 0.5);
    }

    ctx.restore(); // content clip

    // Minimap strip (entries columns only — pickers are always short enough to fit)
    if (!isPicker) {
      drawMinimap(ctx, x, colW, h, items, itemH,
        Array.isArray(col.selectedIds) && col.selectedIds.length
          ? col.selectedIds
          : (col.selectedId != null ? [col.selectedId] : []),
        sy, maxSy, c);
    }

    // Column border right
    ctx.fillStyle = c.line;
    ctx.fillRect(x + colW - 0.5, 0, 0.5, h);

    ctx.restore(); // column clip
  }
}

/**
 * Draw a minimap strip at the right edge of an entries column. Each row
 * contributes a share-keyed bar spanning the column's full scroll range.
 * When the column actually overflows, a translucent viewport rectangle
 * marks the currently visible rows.
 */
function drawMinimap(ctx, x, colW, h, items, itemH, selectedIds, scrollY, maxSy, c) {
  const contentTop = HDR_H;
  const contentH = h - contentTop;
  if (contentH <= 0 || !items.length) return;

  const mmW = MINIMAP_W;
  const mmX = x + colW - mmW;
  const mmY = contentTop;
  const mmH = contentH;

  // Solid background matches the content bg — the left border alone gives
  // the strip its boundary, and bars sit on plain bg (no wash = no reduced
  // contrast between bar tone and background).
  ctx.fillStyle = c.bg;
  ctx.fillRect(mmX, mmY, mmW, mmH);

  // Per-row bars — right-anchored to the strip's right edge so every bar
  // shares a common reference line. Reads like a miniature horizontal bar
  // chart: long bars mean high share, short bars mean low.
  const barPadX = 1;
  const rowStride = mmH / items.length;
  const barH = Math.max(1.2, rowStride);
  const maxBarW = mmW - barPadX * 2;
  const barRight = mmX + mmW - barPadX;
  // Multi-select: any row whose id is in the set should render the
  // active band. Falls back to a single id when the caller passes a
  // string (legacy / single-select call sites).
  const selSet = selectedIds instanceof Set
    ? selectedIds
    : new Set(Array.isArray(selectedIds) ? selectedIds.map(String)
        : (selectedIds != null ? [String(selectedIds)] : []));
  const selectedIndices = [];
  for (let ri = 0; ri < items.length; ri++) {
    const item = items[ri];
    if (selSet.has(String(item.id))) {
      selectedIndices.push(ri);
      continue; // render selected rows in a second pass so they sit on top
    }
    const rowY = mmY + ri * rowStride;
    const share = typeof item.share === "number" ? Math.max(0, Math.min(100, item.share)) : 0;
    // sqrt-compress so mid/low-share rows get visible length while the top
    // rows still reach full width.
    const barW = Math.max(1, Math.sqrt(share / 100) * maxBarW);
    const barY = rowY + (rowStride - barH) / 2;
    // Solid bar tone — idempotent under overlap so a 10K-row list doesn't
    // saturate into a bright slab. Kept muted so green (selected + viewport)
    // stays the dominant accent.
    ctx.fillStyle = c.minimapBar;
    ctx.fillRect(barRight - barW, barY, barW, barH);
  }

  // Second pass: draw every selected row on top with a minimum thickness
  // so each stays legible in dense lists (e.g. 10K sessions where
  // rowStride < 1px).
  if (selectedIndices.length > 0) {
    const SEL_MIN_H = 3;
    for (const idx of selectedIndices) {
      const selRowY = mmY + idx * rowStride;
      const selH = Math.max(rowStride, SEL_MIN_H);
      // Clamp so the selection band stays within the strip even at the tail.
      const selY = Math.min(Math.max(mmY, selRowY - (selH - rowStride) / 2), mmY + mmH - selH);
      // Full-strip tint + full-width solid bar → unambiguous "you are here"
      // regardless of share.
      ctx.fillStyle = c.greenSoft;
      ctx.fillRect(mmX, selY, mmW, selH);
      ctx.fillStyle = c.green;
      ctx.fillRect(mmX + barPadX, selY, maxBarW, selH);
    }
  }

  // Viewport rectangle — only when the column actually overflows.
  if (maxSy > 0) {
    const totalH = items.length * itemH;
    const vpH = Math.max(10, (contentH / totalH) * mmH);
    const travel = mmH - vpH;
    const vpY = mmY + (scrollY / maxSy) * travel;
    ctx.fillStyle = c.ink;
    ctx.globalAlpha = 0.08;
    ctx.fillRect(mmX, vpY, mmW, vpH);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = c.ink;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 1;
    ctx.strokeRect(mmX + 0.5, vpY + 0.5, mmW - 1, vpH - 1);
    ctx.globalAlpha = 1;
  }

  // Solid left border so the strip visually detaches from content to its left.
  ctx.fillStyle = c.line;
  ctx.fillRect(mmX - 0.5, mmY, 1, mmH);
}

/** Scroll arrow buttons at the left/right edges when content overflows. */
function drawScrollArrows(ctx, w, h, cols, state, extraScrollWidth = 0) {
  const maxScrollX = Math.max(0, getTotalW(cols.length, state) + extraScrollWidth - w);
  const { scrollX } = state;
  if (scrollX <= 0 && scrollX >= maxScrollX) return;

  const c = tc();
  const btnW = 24;
  const btnH = 24;
  const btnY = Math.round(h / 2 - btnH / 2);
  const drawArrowBtn = (bx, direction) => {
    ctx.fillStyle = c.bg;
    ctx.fillRect(bx, btnY, btnW, btnH);
    ctx.strokeStyle = c.line;
    ctx.lineWidth = 1;
    ctx.strokeRect(bx + 0.5, btnY + 0.5, btnW - 1, btnH - 1);
    const cx = bx + btnW / 2;
    const cy = btnY + btnH / 2;
    ctx.beginPath();
    if (direction < 0) {
      ctx.moveTo(cx + 2, cy - 4); ctx.lineTo(cx - 2, cy); ctx.lineTo(cx + 2, cy + 4);
    } else {
      ctx.moveTo(cx - 2, cy - 4); ctx.lineTo(cx + 2, cy); ctx.lineTo(cx - 2, cy + 4);
    }
    ctx.strokeStyle = c.muted;
    ctx.lineWidth = 1.3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };
  if (scrollX > 0) drawArrowBtn(6, -1);
  if (scrollX < maxScrollX) drawArrowBtn(w - btnW - 6, 1);
}

// ── Per-entries-column header toolbar (DOM overlay) ──────────────
//
// Each entries column gets a search input + sort dropdown rendered as
// DOM sat on top of the canvas header band, positioned imperatively
// from draw() so the controls track scroll and resize without re-render.
// State is keyed by the same `scope:${parentScope}:${depth}:${dim}`
// cacheKey that ScopeSidebar's DimensionPane uses, so the value flows
// into the existing `columnQueries` plumbing and survives the column ↔
// sidebar view switch.

export function ColumnHeaderToolbar({ depth, dimKey, parentScopePath, cacheRef }) {
  const dim = DIMENSIONS[dimKey] || {};
  const cacheKey = `scope:${encodeScopePath(parentScopePath)}:${depth}:${dimKey}`;
  const [search, setSearch] = useCachedState(cacheRef, cacheKey, "search", "");
  const [sort, setSort] = useCachedState(
    cacheRef,
    cacheKey,
    "sort",
    dim.sortDefault || "activity",
  );
  const sorts = dim.sorts && dim.sorts.length
    ? dim.sorts
    : [["activity", "Most active"], ["name", "A-Z"]];
  const stop = (ev) => { ev.stopPropagation(); };
  return (
    <div
      className="pointer-events-auto flex h-full items-center gap-[6px] pl-[8px] pr-[4px]"
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
    >
      <input
        type="search"
        placeholder={`Filter ${dimensionSentenceLabel(dim, dimKey)}`}
        aria-label={`Filter ${dim.label || dimKey}`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="min-w-0 flex-1 h-[20px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] text-[11px] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
      />
      <Select
        value={sort}
        onChange={setSort}
        options={sorts.map(([v, l]) => ({ value: v, label: l }))}
        ariaLabel="Sort"
        align="right"
        variant="chip"
      />
    </div>
  );
}

// Which dimension did the sidebar tree most-recently expand at the
// trailing scope level? Mirrors markTrailExpansion (SidebarNavRuntime):
// among open dim panes at depth === scopePath.length, pick the one with
// the highest `_trailSeq` stamp. Falls back to the first open dim when no
// stamp is present (e.g. a deep link, where `_trailSeq` isn't persisted).
// Returns null when nothing is expanded there.
function deriveTrailDimFromCache(cache, scopePath) {
  if (!cache) return null;
  const depth = scopePath.length;
  const prefix = encodeScopePath(scopePath);
  let best = null;
  let bestSeq = -Infinity;
  let firstOpen = null;
  for (const dim of DIMENSION_LIST) {
    const v = cache[`scope:${prefix}:${depth}:${dim.key}`];
    if (!v || v.open !== true) continue;
    if (firstOpen == null) firstOpen = dim.key;
    const seq = typeof v._trailSeq === "number" ? v._trailSeq : -Infinity;
    if (seq > bestSeq) { bestSeq = seq; best = dim.key; }
  }
  return best ?? firstOpen;
}

// ── Component ────────────────────────────────────────────────────

export default memo(function ScopeColumns({
  scopePath,
  onChange,
  visible,
  skipColumns = 0,
  initialTrailDim = null,
  flowHaloColor = null,
  cacheRef = null,
  cacheVersion = 0,
}) {
  const caps = useMyPermissions();
  const canvasRef = useRef(null);
  const headerOverlayRef = useRef(null);

  const [trailDim, setTrailDim] = useState(initialTrailDim);

  // Defer the scope path we send to the worker so a rapid scope change (e.g.
  // deep → shallow via a dim picker click) doesn't fire one worker trip with
  // the pre-transition scope and another with the post-transition scope.
  // The canvas still draws at interaction speed using whatever data is latest.
  const deferredScopePath = useDeferredValue(scopePath);
  const deferredTrailDim = useDeferredValue(trailDim);

  // Per-depth extension counter. Each entries column starts with a single
  // page (limit = COLUMN_PAGE_SIZE). When the viewport scrolls within
  // COLUMN_PREFETCH_ROWS of the loaded end, we bump that column's
  // extension by another page and refetch — keyed on depth so it
  // survives column rebuilds for the same drill level. The viewport
  // effect below maps current col indices → depth before reading.
  const [colPageBumps, setColPageBumps] = useState({});  // { [depth]: extraPages }

  // Lazy sparkline overlay. Entries that arrive with `sparkline.series
  // === null` (today: mcp_servers' cheap discovery) get their bucket
  // data fetched per-row when the canvas viewport puts them in view.
  // Stored separately so we don't mutate the useQuery payload; merged
  // in at draw time. Keyed by (dimKey | id | parentScopeKey | range).
  const [lazySparklineMap, setLazySparklineMap] = useState(() => new Map());
  const lazySparklineInflight = useRef(new Set());
  // Reset extension counters when the scope changes — the depth key
  // alone isn't enough to identify "same column" once the user has
  // navigated. The per-depth stickiness only matters within a single
  // scope.
  const scopeKey = useMemo(
    () => deferredScopePath.map((s) => `${s.dim}:${stepIds(s).join("+")}`).join("/"),
    [deferredScopePath],
  );
  useEffect(() => { setColPageBumps({}); }, [scopeKey]);

  // Adopt the sidebar tree's most-recent trailing expansion when this
  // view becomes visible. Expanding Gemini → Clusters in the tree only
  // flips a folder open (it doesn't touch the scope path), so without
  // this the column view would land with no trailing column and make the
  // user re-click the dimension. We only act on the hidden→visible
  // transition and only when the tree actually has an expansion to honor
  // (desired != null) — so a trailing dim the user picked here, in the
  // column view, is never clobbered.
  const prevVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      const desired = deriveTrailDimFromCache(cacheRef?.current, scopePath);
      if (desired && desired !== trailDim) {
        startTransition(() => setTrailDim(desired));
      }
    }
    prevVisibleRef.current = visible;
  }, [visible, scopePath, cacheRef, trailDim]);

  const columnQueries = useMemo(() => {
    const out = {};
    const cache = cacheRef?.current || {};
    const upsertFor = (depth, parentScope, dimKey) => {
      if (!dimKey) return;
      const k = `scope:${encodeScopePath(parentScope)}:${depth}:${dimKey}`;
      const entry = cache[k] || {};
      const search = String(entry.search ?? "").trim();
      const sort = entry.sort;
      const extra = colPageBumps[depth] || 0;
      if (!search && !sort && !extra) return;
      const limit = COLUMN_PAGE_SIZE * (1 + extra);
      const q = { offset: 0, limit };
      if (search) q.search = search;
      if (sort) q.sort = sort;
      out[depth] = q;
    };
    for (let d = 0; d < deferredScopePath.length; d++) {
      const step = deferredScopePath[d];
      if (!step) continue;
      upsertFor(d, deferredScopePath.slice(0, d), step.dim);
    }
    if (deferredTrailDim) {
      upsertFor(deferredScopePath.length, deferredScopePath, deferredTrailDim);
    }
    return out;
  }, [colPageBumps, deferredScopePath, deferredTrailDim, cacheRef, cacheVersion]);

  // Columns + flow links from the worker (single round-trip)
  const range = useCurrentRange();
  // How many top rows the worker should seed flow links for — derived from the
  // canvas height (visible + buffer), measured below. Quantized, so it only
  // re-keys this query on a major resize. Seeded from the window height for a
  // sensible first paint before the canvas is measured.
  const [seedRows, setSeedRows] = useState(
    () => estimateSeedRows(typeof window !== "undefined" ? window.innerHeight : 800),
  );
  const { data: colsData, loading: colsLoading, error: colsError } = useQuery("buildColumnsWithFlowLinks", {
    scopePath: deferredScopePath,
    trailDim: deferredTrailDim,
    skipColumns,
    columnQueries,
    seedRows,
    range,
  }, { enabled: visible });
  // Picker headers display the prior step's name (e.g. "endpoints"
  // column header shows the parent endpoint's label). The initial result
  // resolved that label once at fetch time — if the label
  // cache hadn't filled yet, it baked in the raw UUID. Subscribing to
  // the cache via useLabels and including its tick in the memo deps
  // means we re-resolve picker labels on cache fills.
  const labelTick = useLabels(deferredScopePath);
  const sidebarSeedCols = useMemo(() => {
    const cache = cacheRef?.current;

    const pickerDims = DIMENSION_LIST.map((d) => d.key);
    const seedFor = (parentScope, depth, dimKey) => {
      if (!cache) return null;
      const key = `scope:${encodeScopePath(parentScope)}:${depth}:${dimKey}`;
      const seed = cache[key]?._columnSeed;
      if (!seed || seed.range !== range) return null;
      return seed;
    };
    const pickerCol = (depth, selectedDimKey) => {
      const parentScope = deferredScopePath.slice(0, depth);
      const label = depth === 0
        ? "Overview"
        : scopeStepLabel(deferredScopePath[depth - 1]);
      return {
        type: "picker",
        label,
        depth,
        selectedDimKey,
        items: pickerDims.map((dimKey) => {
          const dim = DIMENSIONS[dimKey] || {};
          const seed = seedFor(parentScope, depth, dimKey);
          return {
            id: dimKey,
            label: dim.label || dimKey,
            count: typeof seed?.count === "number" ? seed.count : "",
            selected: dimKey === selectedDimKey,
          };
        }),
      };
    };

    // Build the column shape that the scope path *implies* — N picker
    // columns interleaved with N entries columns, plus a trailing
    // picker for the next-step dim. We do this even when the scope
    // path is empty (Overview) so the canvas can render the single
    // picker column immediately rather than falling through to the
    // viewport-filling skeleton fallback.
    //
    // Entries columns are populated only when the sidebar's column
    // seed for that step is available; otherwise we emit a placeholder
    // with `entries: []` so the renderer can show that specific column
    // as a focused skeleton (vs. spraying skeletons across the whole
    // viewport).
    const cols = [];
    for (let depth = 0; depth < deferredScopePath.length; depth += 1) {
      const step = deferredScopePath[depth];
      const parentScope = deferredScopePath.slice(0, depth);
      const seed = seedFor(parentScope, depth, step.dim);
      cols.push(pickerCol(depth, step.dim));
      const ids = stepIds(step).map(String);
      const seedEntries = seed?.entries?.length ? seed.entries : null;
      cols.push({
        type: "entries",
        label: DIMENSIONS[step.dim]?.label || step.dim,
        entries: seedEntries || [],
        total: seedEntries ? (Number(seed.total) || seedEntries.length) : 0,
        offset: 0,
        limit: seedEntries ? seedEntries.length : 0,
        selectedIds: ids,
        selectedId: step.id ?? ids[0] ?? null,
        dimKey: step.dim,
        depth,
        // Flag for the renderer: this column is structurally known but
        // its rows haven't arrived yet. Lets the canvas paint a focused
        // skeleton in just this column instead of viewport-wide.
        pending: !seedEntries,
      });
    }
    cols.push(pickerCol(deferredScopePath.length, deferredTrailDim));
    // Trailing entries column from the sidebar seed. When the tree
    // already expanded this dim, its entries are sitting in the cache —
    // render them straight away so crossing into the column view doesn't
    // blank out and refetch what was just fetched. The worker response
    // replaces this once it lands (same shape).
    if (deferredTrailDim) {
      const trailSeed = seedFor(deferredScopePath, deferredScopePath.length, deferredTrailDim);
      const trailEntries = trailSeed?.entries?.length ? trailSeed.entries : null;
      if (trailEntries) {
        cols.push({
          type: "entries",
          label: DIMENSIONS[deferredTrailDim]?.label || deferredTrailDim,
          entries: trailEntries,
          total: Number(trailSeed.total) || trailEntries.length,
          offset: 0,
          limit: trailEntries.length,
          selectedIds: [],
          selectedId: null,
          dimKey: deferredTrailDim,
          depth: deferredScopePath.length,
          pending: false,
        });
      }
    }
    return skipColumns > 0 ? cols.slice(skipColumns) : cols;
  }, [cacheRef, cacheVersion, deferredScopePath, deferredTrailDim, range, skipColumns, labelTick]);

  // Stabilize the columns reference: `colsData?.cols || []` returns a
  // fresh array literal on every render when cols is missing, and useMemo
  // pins it across renders when the response is stable. Without this the
  // viewport-fetch effect's dep would change every render, causing
  // setFlowLinks → re-render → effect → setFlowLinks → ... infinite loop.
  //
  // Once we have any colsData, keep showing it through subsequent refetches
  // (sort/search/page-bump). Falling back to sidebarSeedCols during a refetch
  // would blank out columns whose seed doesn't carry entries for the new
  // query — the user perceives the column as "disappearing". The stale
  // detection below adds a dim overlay so the user still sees that the
  // column is loading.
  const usingSidebarSeed = !colsData?.cols && sidebarSeedCols.length > 0;
  const columns = useMemo(() => {
    let raw = colsData?.cols ? colsData.cols : sidebarSeedCols;
    // Optimistic pop: when the user clears the trailing dim ("← PICK
    // DIMENSION"), the trail listing in `raw` is stale until the refetch lands.
    // Drop it now so the dimension picker (always built into the response)
    // shows immediately instead of the frozen flow view. Safe: a null trailDim
    // never carries a trailing entries column once the response resolves.
    //
    // Only pop a *trail preview* — an entries column beyond the committed scope
    // (depth === scopePath.length). An entry click also nulls trailDim, but it
    // grows the scope, so the live scopePath now commits that column at
    // depth < scopePath.length (`raw` still lags on deferredScopePath, so its
    // trailing entries depth predates the click). Popping it there would shrink
    // `columns` below the pending skeleton's insertIndex, clamping the skeleton
    // back over the column the user just clicked (O-1781).
    const lastRaw = raw.length ? raw[raw.length - 1] : null;
    if (trailDim == null && lastRaw?.type === "entries" && (lastRaw.depth ?? 0) >= scopePath.length) {
      raw = raw.slice(0, -1);
    }
    return raw.map((col) => {
      if (col.type !== "picker") {
        const depth = col.depth ?? 0;
        const liveStep = scopePath[depth];
        const liveIds = liveStep?.dim === col.dimKey
          ? stepIds(liveStep).map(String)
          : [];
        const currentIds = Array.isArray(col.selectedIds) && col.selectedIds.length
          ? col.selectedIds.map(String)
          : (col.selectedId != null ? [String(col.selectedId)] : []);
        const sameSelection = liveIds.length === currentIds.length
          && liveIds.every((id, idx) => id === currentIds[idx]);
        if (sameSelection) return col;
        return {
          ...col,
          selectedIds: liveIds,
          selectedId: liveIds.length ? (liveStep?.id ?? liveIds[0]) : null,
        };
      }
      const depth = col.depth ?? 0;
      const label = depth === 0
        ? "Overview"
        : scopeStepLabel(deferredScopePath[depth - 1]);
      // Only allocate a new object when the label actually changed,
      // so reference stability holds for already-resolved pickers.
      return label === col.label ? col : { ...col, label };
    });
  }, [colsData, sidebarSeedCols, usingSidebarSeed, scopePath, deferredScopePath, labelTick, trailDim]);
  const columnsFromSidebarSeed = usingSidebarSeed;

  // Flow links are fetched separately (viewport-driven) — see effect
  // below. The columns response carries an empty array so we can keep
  // the same drawFlowLinks contract.
  const [flowLinks, setFlowLinks] = useState([]);
  // True while a viewport-driven flow-links query is in flight.
  // Used only for missing bridge indicators. Existing flow lines stay at
  // full strength during unrelated downstream fetches.
  const [flowLoading, setFlowLoading] = useState(false);
  const flowInflightRef = useRef(0);
  // Bump on any scroll change so the viewport fetcher debounce fires.
  // Loading is NOT toggled here — it's coverage-based: we only show a
  // missing-bridge indicator when the visible viewport has actually
  // scrolled past what the previous fetch covered. Tiny scrolls within
  // the prefetched buffer just trigger an opportunistic background
  // refetch with no visual disruption.
  const [scrollTick, setScrollTick] = useState(0);
  const bumpScrollTick = useCallback(() => {
    setScrollTick((n) => n + 1);
    // Any vertical scroll detaches the hover tooltip from its row;
    // hide it and let the next mouse move re-anchor at the new spot.
    setHoverTooltip(null);
  }, []);
  const flowFetchTimerRef = useRef(null);
  const flowFetchSigRef = useRef("");
  // Per-entries-column window we last successfully fetched. Compared
  // against the current visible viewport on each scroll to decide
  // whether to show bridge loading. This is keyed by row windows, not by
  // scopePath — flow links deliberately ignore dashboard scope and only
  // depend on the visible dimension/id pairs.
  const lastFetchedWindowsRef = useRef({});

  // Track which scope + per-column query produced the current columns so we
  // can detect stale columns. When loading, compare the live params to the
  // confirmed ones to identify which column indices are pending.
  const confirmedScopeRef = useRef(scopePath);
  const confirmedColumnQueriesRef = useRef({});
  if (!colsLoading && colsData) {
    confirmedScopeRef.current = scopePath;
    confirmedColumnQueriesRef.current = columnQueries;
  }

  // Hidden flow-bridge picker columns (flow links present, or "no flow
  // available") that pop back to the dimension picker when clicked. Set each
  // draw so handleClick can detect them without recomputing the bridge math.
  const hiddenFlowColsRef = useRef(null);

  // Compute stale column set: columns whose content will change when the worker responds.
  // Passed to drawGrid via ref to avoid adding dependencies to the draw callback.
  const loadingColsRef = useRef(null);
  {
    let staleSet = null;
    if (colsLoading && colsData && !columnsFromSidebarSeed) {
      const confirmed = confirmedScopeRef.current;
      // Find the first scope step that differs
      let diverge = 0;
      while (diverge < confirmed.length && diverge < scopePath.length &&
             confirmed[diverge]?.dim === scopePath[diverge]?.dim &&
             confirmed[diverge]?.id === scopePath[diverge]?.id) {
        diverge++;
      }
      // A pure append (confirmed is a prefix of the new path; the scope only
      // grew deeper) changes no existing column — the list the user drilled
      // into keeps its rows, and only a new column appends (shown by the
      // pending skeleton). Dimming the existing columns there is the
      // "loads across the whole canvas" feel; skip it. Same-dim id changes,
      // dimension swaps and truncation still invalidate downstream columns.
      const isPureAppend = diverge === confirmed.length && scopePath.length > confirmed.length;
      if (!isPureAppend
          && (diverge < scopePath.length || diverge < confirmed.length || scopePath.length !== confirmed.length)) {
        // If the user picked a different row in the same dimension, that
        // entries listing is still valid; only its downstream columns need
        // replacement. Dimension changes or scope truncation invalidate the
        // column at the divergence point too.
        const sameDimAtDivergence = confirmed[diverge]?.dim
          && confirmed[diverge]?.dim === scopePath[diverge]?.dim;
        const staleFromDepth = sameDimAtDivergence ? diverge + 1 : diverge;
        // Map stale depth to column indices.
        staleSet = new Set();
        for (let ci = 0; ci < columns.length; ci++) {
          if ((columns[ci].depth ?? 0) >= staleFromDepth) staleSet.add(ci);
        }
      }
      // Also flag entries columns whose per-column search or sort changed
      // since the last confirmed fetch — the scope-divergence check above
      // won't catch a filter or sort change at an unchanged depth. Page-bumps
      // (limit-only change) are deliberately excluded: they extend existing
      // rows, so the current view is still valid while the longer page lands.
      const confirmedQ = confirmedColumnQueriesRef.current || {};
      for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci];
        if (col.type !== "entries") continue;
        const d = col.depth ?? 0;
        const cur = columnQueries[d] || {};
        const prev = confirmedQ[d] || {};
        if ((cur.search || "") !== (prev.search || "")
            || (cur.sort || "") !== (prev.sort || "")) {
          if (!staleSet) staleSet = new Set();
          staleSet.add(ci);
        }
      }
      // Optimistic pop to the dimension picker: the stale trailing entries was
      // sliced so the picker shows immediately, but its counts are still being
      // confirmed by the refetch. Flag it loading (dim) until the response
      // lands — the requested loader for when the count cache has expired.
      // Only for a genuine trail-preview pop (← PICK DIMENSION, trailing entries
      // at depth >= scopePath.length). A value click also nulls trailDim but
      // keeps that column as a committed selection (depth < scopePath.length),
      // which must stay bright — see the matching guard in the columns memo.
      const rawCols = colsData.cols;
      const lastRawCol = rawCols?.length ? rawCols[rawCols.length - 1] : null;
      if (trailDim == null && lastRawCol?.type === "entries"
          && (lastRawCol.depth ?? 0) >= scopePath.length && columns.length) {
        if (!staleSet) staleSet = new Set();
        staleSet.add(columns.length - 1);
      }
    }
    loadingColsRef.current = staleSet;
  }

  // Seed flow links from the columns response. buildColumnsWithFlowLinks
  // resolves the adjacent-column links for the top window in the same
  // round-trip (it already holds every entry id), so a freshly revealed
  // column paints its links immediately instead of waiting for this
  // component to render, fire a second getFlowLinks trip, and flash the
  // "LOADING LINKS" overlay (O-1888 follow-up). When the seed covers the
  // current viewport we prime the dedup refs so the viewport effect below
  // no-ops; a deep-scrolled column (visible window past the seed) is left
  // for that effect to fetch normally. Declared before that effect so it
  // runs first on a colsData change.
  const flowSeedConsumedRef = useRef(null);
  useEffect(() => {
    if (!colsData?.flowSeeded) return;
    if (flowSeedConsumedRef.current === colsData) return;  // consume each response once
    flowSeedConsumedRef.current = colsData;
    const h = canvasRef.current?.clientHeight ?? 0;
    const windows = h ? computeEntriesWindows(columns, st.current, h) : [];
    const seedRows = colsData.flowSeedRows || 0;
    const covers = h && windows.every((w) => !w || (w.firstRow + w.ids.length) <= seedRows);
    // Viewport already scrolled past the seed: don't clobber existing links
    // with a partial top-window seed — let the effect below fetch the real
    // window (its loader is correct there, we genuinely lack the data).
    if (h && !covers) return;
    setFlowLinks(colsData.flowLinks || []);
    if (!h) return;  // painted the seed; can't verify coverage yet — effect below reconciles
    const localPairs = buildAdjacentPairs(columns, windows);
    if (!localPairs.length) return;
    // Match the viewport effect's signature/window bookkeeping exactly so
    // its first run dedups to a no-op (no redundant trip, no overlay).
    flowFetchSigRef.current = JSON.stringify({
      pairs: localPairs.map((p) => ({ ad: p.aDim, ai: p.aIds, bd: p.bDim, bi: p.bIds })),
      range,
    });
    for (const win of windows) {
      if (!win) continue;
      lastFetchedWindowsRef.current[win.ci] = {
        firstRow: win.firstRow,
        lastRow: win.firstRow + win.ids.length - 1,
      };
    }
    setFlowLoading(false);
  }, [colsData, columns, range]);

  // Viewport-driven flow-link fetch. Two ideas working together:
  //
  //  1) Coverage-based loading. We only flip flowLoading=true when the
  //     visible viewport has scrolled past what the previous fetch
  //     covered. Small movements within the prefetched buffer trigger
  //     a quiet background refetch (or a sig-deduped no-op) with no
  //     disruption to already-rendered links.
  //
  //  2) Always batch + always opportunistically refetch. Even when the
  //     visible window is fully covered, the buffered window may be
  //     drifting toward an edge — fire the fetch anyway so the buffer
  //     stays loaded in the direction we're scrolling.
  useEffect(() => {
    if (flowFetchTimerRef.current) clearTimeout(flowFetchTimerRef.current);
    if (!visible) {
      setFlowLoading(false);
      return undefined;
    }
    if (!columns.length) {
      setFlowLinks([]);
      flowFetchSigRef.current = "";
      lastFetchedWindowsRef.current = {};
      setFlowLoading(false);
      return undefined;
    }

    // Coverage check: can the current visible viewport be drawn from
    // the rows the previous fetch actually returned, and is it the same
    // visible dim/id pair signature? If yes, leave loading off; otherwise
    // set loading now (don't wait for the network). The signature excludes
    // scopePath because the flow endpoint ignores scope; selecting a new
    // downstream row should not invalidate the already-visible upstream
    // relationship.
    const hNow = canvasRef.current?.clientHeight ?? 0;
    if (hNow) {
      const windows = computeEntriesWindows(columns, st.current, hNow);
      const localPairs = buildAdjacentPairs(columns, windows);
      const sig = JSON.stringify({
        pairs: localPairs.map((p) => ({ ad: p.aDim, ai: p.aIds, bd: p.bDim, bi: p.bIds })),
        range,
      });
      const contentH = Math.max(0, hNow - HDR_H);
      const fully = columns.every((col, ci) => {
        if (col.type !== "entries") return true;
        const items = col.entries || [];
        if (items.length === 0) return true;
        const sy = st.current.colScrollY?.[ci] || 0;
        const visFirst = Math.max(0, Math.floor(sy / ENTRY_H));
        const visLast = Math.min(items.length - 1, Math.floor((sy + contentH) / ENTRY_H));
        const fetched = lastFetchedWindowsRef.current[ci];
        return fetched && visFirst >= fetched.firstRow && visLast <= fetched.lastRow;
      });
      if (localPairs.length > 0 && (!fully || sig !== flowFetchSigRef.current)) setFlowLoading(true);
    }

    // 80 ms keeps fetches responsive during continuous scroll without
    // hammering the server on every wheel tick.
    flowFetchTimerRef.current = setTimeout(async () => {
      const h = canvasRef.current?.clientHeight ?? 0;
      if (!h) return;
      const windows = computeEntriesWindows(columns, st.current, h);
      const localPairs = buildAdjacentPairs(columns, windows);
      const sig = JSON.stringify({
        pairs: localPairs.map((p) => ({ ad: p.aDim, ai: p.aIds, bd: p.bDim, bi: p.bIds })),
        range,
      });
      if (sig === flowFetchSigRef.current) {
        if (flowInflightRef.current === 0) setFlowLoading(false);
        return;
      }
      flowFetchSigRef.current = sig;
      if (localPairs.length === 0) {
        setFlowLinks([]);
        if (flowInflightRef.current === 0) setFlowLoading(false);
        return;
      }
      const mySig = sig;
      flowInflightRef.current += 1;
      try {
        const res = await query("getFlowLinks", {
          scopePath: deferredScopePath,
          pairs: localPairs.map((p) => ({
            aDim: p.aDim, aIds: p.aIds, bDim: p.bDim, bIds: p.bIds,
          })),
          range,
        });
        // A faster newer fetch may have already settled while we were in
        // flight. Apply only if our request is still the latest one.
        if (mySig === flowFetchSigRef.current) {
          setFlowLinks(reshapeFlowLinks(res, localPairs, columns));
          // Record the rows this fetch actually covers so the next
          // scroll's coverage check can decide whether to show loading.
          for (const win of windows) {
            if (!win) continue;
            lastFetchedWindowsRef.current[win.ci] = {
              firstRow: win.firstRow,
              lastRow: win.firstRow + win.ids.length - 1,
            };
          }
        }
      } catch {
        // Network blip — keep last good links rather than blanking.
      } finally {
        flowInflightRef.current = Math.max(0, flowInflightRef.current - 1);
        if (flowInflightRef.current === 0) setFlowLoading(false);
      }
    }, 80);
    return () => {
      if (flowFetchTimerRef.current) clearTimeout(flowFetchTimerRef.current);
    };
  }, [scrollTick, columns, columnsFromSidebarSeed, deferredScopePath, range, visible]);

  // Per-entries-column viewport pagination. When the user scrolls within
  // COLUMN_PREFETCH_ROWS of the loaded end of any entries column, bump
  // that column's depth in colPageBumps so the next columns query
  // asks for a larger limit. Server-side: COLUMN_PAGE_SIZE *
  // (1 + extra) rows. Reset on scope change happens above when scopeKey
  // changes.
  useEffect(() => {
    if (!visible) return;
    if (!columns.length) return;
    const hNow = canvasRef.current?.clientHeight ?? 0;
    if (!hNow) return;
    const contentH = Math.max(0, hNow - HDR_H);
    setColPageBumps((prev) => {
      let next = prev;
      for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci];
        if (col.type !== "entries") continue;
        const items = col.entries || [];
        const total = Number(col.total) || items.length;
        // Already loaded all rows? Nothing more to fetch regardless of scroll.
        if (items.length >= total) continue;
        const sy = st.current.colScrollY?.[ci] || 0;
        const visLast = Math.floor((sy + contentH) / ENTRY_H);
        // Threshold: when the last visible row is within
        // COLUMN_PREFETCH_ROWS of the loaded end, ask for another page.
        if (visLast < items.length - COLUMN_PREFETCH_ROWS) continue;
        const depth = col.depth ?? 0;
        const cur = prev[depth] ?? 0;
        // The current loaded set fits `(cur+1)*COLUMN_PAGE_SIZE` rows.
        // Only bump when we've actually consumed (most of) what's on
        // hand — guards against bumps before the first page lands.
        if (items.length < (cur + 1) * COLUMN_PAGE_SIZE - COLUMN_PREFETCH_ROWS) continue;
        if (next === prev) next = { ...prev };
        next[depth] = cur + 1;
      }
      return next;
    });
  }, [scrollTick, columns, visible]);

  // Lazy sparkline viewport fetcher. Mirrors the page-bump effect
  // above: on scroll/columns changes, walk visible-ish rows of each
  // entries column; for any item carrying the `sparkline.series ===
  // null` sentinel, kick off a per-entry getDimEntrySparkline. Inflight
  // and resolved keys are tracked separately so we don't refire.
  useEffect(() => {
    if (!visible || !columns.length) return;
    const hNow = canvasRef.current?.clientHeight ?? 0;
    if (!hNow) return;
    const contentH = Math.max(0, hNow - HDR_H);
    const BUFFER_ROWS = 8;
    for (let ci = 0; ci < columns.length; ci++) {
      const col = columns[ci];
      if (col.type !== "entries") continue;
      const items = col.entries || [];
      if (items.length === 0) continue;
      const sy = st.current.colScrollY?.[ci] || 0;
      const firstRow = Math.max(0, Math.floor(sy / ENTRY_H) - BUFFER_ROWS);
      const lastRow = Math.min(items.length - 1, Math.ceil((sy + contentH) / ENTRY_H) + BUFFER_ROWS);
      const parentScopePath = deferredScopePath.slice(0, col.depth || 0);
      const scopeKey = encodeScopePath(parentScopePath);
      for (let ri = firstRow; ri <= lastRow; ri++) {
        const item = items[ri];
        if (!item || item.sparkline?.series !== null) continue;
        const key = `${col.dimKey}|${item.id}|${scopeKey}|${range}`;
        if (lazySparklineMap.has(key)) continue;
        if (lazySparklineInflight.current.has(key)) continue;
        lazySparklineInflight.current.add(key);
        // staleMs matches dataClient's DEFAULT_STALE_MS so the warm
        // cache the sidebar's useQuery wrote is reused — without this,
        // dragging into the columns view re-fetches every sparkline
        // the sidebar just resolved. cache:true keeps this view's
        // resolutions warm for the inverse transition.
        query("getDimEntrySparkline", {
          dimKey: col.dimKey,
          id: item.id,
          parentScopePath,
          range,
        }, { cache: true, staleMs: 30_000 }).then((data) => {
          lazySparklineInflight.current.delete(key);
          if (!Array.isArray(data?.series)) return;
          setLazySparklineMap((prev) => {
            const next = new Map(prev);
            next.set(key, data.series);
            return next;
          });
        }).catch(() => {
          lazySparklineInflight.current.delete(key);
        });
      }
    }
  }, [scrollTick, columns, visible, range, deferredScopePath, lazySparklineMap]);

  // True when any visible entries column still carries rows whose lazy
  // sparkline hasn't resolved. Drives the RAF tick below so the
  // canvas can animate the pending baseline shimmer while we wait.
  const hasPendingSparklines = useMemo(() => {
    for (const col of columns) {
      if (col.type !== "entries") continue;
      const items = col.entries || [];
      const parentScopeKey = encodeScopePath(deferredScopePath.slice(0, col.depth || 0));
      for (const item of items) {
        if (item?.sparkline?.series !== null) continue;
        const key = `${col.dimKey}|${item.id}|${parentScopeKey}|${range}`;
        if (!lazySparklineMap.has(key)) return true;
      }
    }
    return false;
  }, [columns, lazySparklineMap, deferredScopePath, range]);

  // Track pending navigation so we can show a skeleton column.
  // Set on entry click, cleared when NEW column data arrives (not stale data).
  const pendingNavRef = useRef(null); // { prevDataId } or null

  // Interaction state (no React renders needed)
  const st = useRef({ scrollX: 0, colScrollY: {}, colWidths: {}, hoverCol: -1, hoverRow: -1, rafId: 0 });
  const resizeRef = useRef(null); // { ci, startX, startW } during drag

  // ── Draw ───────────────────────────────────────────────────────

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const pending = pendingNavRef.current;
    const pendingIndex = pending
      ? Math.max(0, Math.min(pending.insertIndex ?? columns.length, columns.length))
      : -1;
    const drawColumns = pending ? columns.slice(0, pendingIndex) : columns;
    const pendingSkeletonWidth = pending
      ? (pendingIndex < columns.length ? getColW(pendingIndex, st.current) : DEFAULT_COL_W)
      : 0;
    // While a picker-dim nav is in flight, the picker the user just clicked
    // (one column left of the inserting skeleton) gets a loading treatment:
    // its selected row glimmers and the rest dim, signalling that this list
    // of choices is being rebuilt too — not just the column to its right.
    const loadingPickerIdx = pending && drawColumns[pendingIndex - 1]?.type === "picker"
      ? pendingIndex - 1
      : -1;
    const loadingPickerSelId = loadingPickerIdx >= 0 ? pending.selectedId : null;
    // Value click (entry row): the column the user clicked out of is an entries
    // list, not a picker — glimmer the chosen row there instead. The new column
    // appends as the skeleton; the existing columns are left untouched.
    const loadingEntryIdx = pending && drawColumns[pendingIndex - 1]?.type === "entries"
      ? pendingIndex - 1
      : -1;
    const loadingEntrySelId = loadingEntryIdx >= 0 ? pending.selectedId : null;
    const flowBridgePairs = buildFlowBridgePairs(drawColumns);
    const flowLinkPairKeys = new Set();
    for (const link of flowLinks) {
      if (drawColumns[link.colA] && drawColumns[link.colB]) {
        flowLinkPairKeys.add(`${link.colA}:${link.colB}`);
      }
    }
    // A bridge pair with no links: still loading → "LOADING LINKS"; settled →
    // "(no flow available)". Either way the picker between is hidden, so the
    // column consistently reads as "flow" rather than leaking the dim picker.
    const isLoadingFlows = columnsFromSidebarSeed || flowLoading;
    const linklessBridgePairs = flowBridgePairs.filter((pair) => !flowLinkPairKeys.has(`${pair.colA}:${pair.colB}`));
    const loadingBridgePairs = isLoadingFlows ? linklessBridgePairs : [];
    const noFlowBridgePairs = isLoadingFlows ? [] : linklessBridgePairs;

    // Identify picker columns that should hide content (flow links / "no flow"
    // fill that space). interactiveFlowCols are the ones a click pops out of
    // (links present or "no flow") — loading bridges are transient, not clickable.
    const hiddenPickerCols = new Set();
    const interactiveFlowCols = new Set();
    const hideBridge = (colA, colB, interactive) => {
      for (let ci = colA + 1; ci < colB; ci += 1) {
        if (drawColumns[ci]?.type !== "picker") continue;
        hiddenPickerCols.add(ci);
        if (interactive) interactiveFlowCols.add(ci);
      }
    };
    for (const link of flowLinks) {
      if (drawColumns[link.colA] && drawColumns[link.colB]) hideBridge(link.colA, link.colB, true);
    }
    for (const pair of loadingBridgePairs) hideBridge(pair.colA, pair.colB, false);
    for (const pair of noFlowBridgePairs) hideBridge(pair.colA, pair.colB, true);
    hiddenFlowColsRef.current = interactiveFlowCols;

    drawGrid(ctx, w, h, drawColumns, st.current, hiddenPickerCols, pendingSkeletonWidth, -1, {
      lazySparklineMap,
      deferredScopePath,
      range,
      loadingPickerIdx,
      loadingPickerSelId,
      loadingEntryIdx,
      loadingEntrySelId,
    });
    // Hover/drag treatment for the flow column: grey + fade the links and show
    // the pop-out label. Greying is a tone swap inside drawFlowLinks (not a
    // canvas filter), so it's cheap enough to hold every frame while panning.
    const hoveringFlowCol = interactiveFlowCols.has(st.current.hoverCol);
    if (loadingBridgePairs.length) drawFlowLoadingBridges(ctx, w, h, drawColumns, st.current, loadingBridgePairs);
    if (noFlowBridgePairs.length) drawNoFlowBridges(ctx, w, h, drawColumns, st.current, noFlowBridgePairs, hoveringFlowCol ? st.current.hoverCol : -1);
    if (flowLinks.length > 0) {
      if (hoveringFlowCol) { ctx.save(); ctx.globalAlpha = 0.22; }
      drawFlowLinks(ctx, w, h, drawColumns, st.current, flowLinks, flowHaloColor, hoveringFlowCol);
      if (hoveringFlowCol) ctx.restore();
    }
    // Pop-out affordance over a hovered/dragged flow column (above the curves).
    if (hoveringFlowCol) drawPopOutAffordance(ctx, w, h, st.current.hoverCol, st.current, drawColumns);
    // Arrows draw last so they sit above the flow curves.
    drawScrollArrows(ctx, w, h, drawColumns, st.current, pendingSkeletonWidth);

    // Loading overlays + pending click indicator use theme colors
    const colors = tc();
    const drawSkeletonColumn = (skX, skW, rowCount = 6, label = "") => {
      if (skX > w || skX + skW < 0) return;
      ctx.fillStyle = colors.bg;
      ctx.fillRect(skX, 0, skW, h);
      if (label) {
        ctx.font = "500 10px Inter,system-ui,sans-serif";
        ctx.letterSpacing = "0.12em";
        ctx.fillStyle = colors.muted;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(`LOADING ${label}`.toUpperCase(), skX + PX, HDR_H / 2 + 1);
        ctx.letterSpacing = "0px";
      } else {
        ctx.fillStyle = colors.lineSoft;
        ctx.fillRect(skX + PX, HDR_H / 2 - 4, 50, 8);
      }
      for (let ri = 0; ri < rowCount; ri++) {
        const ry = HDR_H + ri * ENTRY_H;
        if (ry > h) break;
        const alpha = 0.5 - ri * 0.055;
        ctx.globalAlpha = Math.max(0.14, alpha);
        ctx.fillStyle = colors.lineSoft;
        ctx.fillRect(skX + PX, ry + 10, skW * (0.45 + ((ri * 17) % 25) / 100), 10);
        ctx.fillRect(skX + PX, ry + 26, skW * (0.55 + ((ri * 13) % 20) / 100), 10);
        ctx.globalAlpha = 1;
        ctx.fillStyle = colors.lineSoft;
        ctx.fillRect(skX, ry + ENTRY_H - 0.5, skW, 0.5);
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors.line;
      ctx.fillRect(skX + skW - 0.5, 0, 0.5, h);
    };

    // Skeleton fallback only fires when we have no idea what shape
    // the columns should take — i.e. truly empty before the first
    // sidebar-seed or server response. With the scope-path-derived
    // seed above this only happens during the very first paint of an
    // uninitialised cache, which is rare. When it does fire, scope a
    // sensible width (one column) rather than spraying the viewport.
    if (colsLoading && columns.length === 0) {
      drawSkeletonColumn(0, DEFAULT_COL_W, 8);
      return;
    }

    // Draw loading overlay on stale columns
    const staleCols = loadingColsRef.current;
    if (staleCols) {
      const { scrollX: sx } = st.current;
      for (const ci of staleCols) {
        if (ci >= drawColumns.length) continue;
        const colW = getColW(ci, st.current);
        const cx = getColX(ci, st.current) - sx;
        if (cx + colW < 0 || cx > w) continue;
        ctx.fillStyle = colors.bg;
        ctx.globalAlpha = 0.55;
        ctx.fillRect(cx, HDR_H, colW, h - HDR_H);
        ctx.globalAlpha = 1;
      }
    }

    // Draw skeleton column where the new column will appear
    if (pendingNavRef.current) {
      drawSkeletonColumn(
        getTotalW(drawColumns.length, st.current) - st.current.scrollX,
        pendingSkeletonWidth,
        6,
        pendingNavRef.current.label || "",
      );
    }

    // Position the DOM header toolbars over their entries columns.
    // Run after canvas draw so the controls follow scroll + resize
    // without going through React.
    const overlay = headerOverlayRef.current;
    if (overlay) {
      const sx = st.current.scrollX;
      for (const child of overlay.children) {
        const ci = Number(child.dataset.colIdx);
        if (Number.isNaN(ci) || ci < 0 || ci >= drawColumns.length) {
          child.style.visibility = "hidden";
          continue;
        }
        const colX = getColX(ci, st.current) - sx;
        const colW = getColW(ci, st.current);
        // Reserve the rightmost MINIMAP_EDGE_RESERVE so the canvas can
        // still hit-test the column-resize border at the edge.
        const innerW = Math.max(0, colW - MINIMAP_EDGE_RESERVE);
        child.style.transform = `translate3d(${colX}px, 0, 0)`;
        child.style.width = `${innerW}px`;
        child.style.visibility = "visible";
      }
    }
  }, [columns, columnsFromSidebarSeed, flowLinks, flowLoading, flowHaloColor, colsLoading, lazySparklineMap, deferredScopePath, range]);

  // Always run the LATEST draw from a deferred frame. A frame scheduled during
  // loading captured `columns` by value (e.g. the pre-resolve 3-column set) but
  // reads pendingNavRef live; once the nav resolves (pendingNavRef → null) that
  // stale frame would treat its captured columns as the complete set, compute a
  // too-small total width, and the draw-time clamp would zero scrollX. Routing
  // through a ref makes the leftover frame draw with the current columns.
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const scheduleDraw = useCallback(() => {
    if (st.current.rafId) return;
    st.current.rafId = requestAnimationFrame(() => { st.current.rafId = 0; drawRef.current(); });
  }, []);

  // Redraw when the color theme flips. Changing `data-theme` swaps the CSS
  // vars but doesn't itself trigger a canvas draw — so the columns kept the
  // old palette until a hover/scroll/resize forced one. Bump on the shared
  // theme version and null the cached palette so the next draw reads fresh
  // colors. This is the ONLY place `_tc` needs resetting: the palette
  // depends on nothing but the theme, and re-reading it per resize tick
  // forces a style recalc against a just-mutated DOM on every frame of a
  // divider drag.
  const themeVersion = useThemeVersion();
  useEffect(() => { _tc = null; scheduleDraw(); }, [themeVersion, scheduleDraw]);

  // Draw on mount / data change / resize. Resize redraws are
  // burst-coalesced: a divider drag fires a ResizeObserver tick every
  // frame, and a full strip repaint + GPU texture upload per tick is
  // what saturated weaker GPUs in the staging traces. Mid-burst the
  // canvas is pinned to the pixel size it was last drawn at — the
  // wrapper's overflow-hidden clips it rather than letting CSS stretch
  // the bitmap — and one crisp redraw lands when the size settles.
  const drawnSizeRef = useRef({ w: 0, h: 0 });
  useEffect(() => {
    const canvas = canvasRef.current;
    // Clear any mid-burst pin left by a previous observer generation —
    // a data-driven re-run during a drag would otherwise strand the
    // canvas at a stale pinned size with no resize tick left to settle.
    if (canvas) {
      canvas.style.width = "";
      canvas.style.height = "";
    }
    draw();
    if (!canvas) return;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (w > 0 && h > 0) drawnSizeRef.current = { w, h };
    return observeSettledSize(
      canvas,
      () => {
        canvas.style.width = "";
        canvas.style.height = "";
        draw();
      },
      {
        onBurstStart: () => {
          const dpr = window.devicePixelRatio || 1;
          canvas.style.width = `${Math.round(canvas.width / dpr)}px`;
          canvas.style.height = `${Math.round(canvas.height / dpr)}px`;
        },
      },
    );
  }, [draw]);

  // Measure the canvas to size the flow-link seed (visible rows + buffer, see
  // estimateSeedRows). Quantized, so this only re-keys the columns query on a
  // major resize — and a taller finder seeds more rows, never truncated.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const measure = () => {
      const next = estimateSeedRows(canvas.clientHeight || 0);
      setSeedRows((prev) => (prev === next ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // Redraw when toggled from hidden to visible
  useEffect(() => {
    if (!visible) return;
    requestAnimationFrame(() => drawRef.current());
  }, [visible]);

  // Sparkline-pending shimmer ticker. While any visible row carries
  // an unresolved lazy sparkline, drive a steady RAF that schedules
  // a redraw each frame — drawGrid's pending branch reads
  // performance.now() to animate the shimmer band. Stops the moment
  // every pending entry has resolved so we don't burn CPU at idle.
  useEffect(() => {
    if (!visible || !hasPendingSparklines) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      scheduleDraw();
      requestAnimationFrame(tick);
    };
    const rafId = requestAnimationFrame(tick);
    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, [visible, hasPendingSparklines, scheduleDraw]);

  // Redraw when loading state changes (to update per-column overlays)
  useEffect(() => {
    scheduleDraw();
  }, [colsLoading, scheduleDraw]);

  // Glimmer ticker. While a picker-dim or value (entries) nav is loading, drive
  // a steady RAF so the chosen row's glimmer sweep animates (drawGrid reads
  // performance.now()). Only runs for those navs — other loads (initial, sort,
  // page-bump) rely on the one-shot redraw above. Stops the instant the
  // response lands so we don't burn CPU at idle.
  useEffect(() => {
    if (!visible || !colsLoading) return undefined;
    const p = pendingNavRef.current;
    const idx = p ? (p.insertIndex ?? 0) - 1 : -1;
    const srcType = columns[idx]?.type;
    if (idx < 0 || (srcType !== "picker" && srcType !== "entries")) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      scheduleDraw();
      requestAnimationFrame(tick);
    };
    const rafId = requestAnimationFrame(tick);
    return () => { cancelled = true; cancelAnimationFrame(rafId); };
  }, [visible, colsLoading, columns, scheduleDraw]);

  // Clear pending nav skeleton when genuinely new column data arrives
  const justResolvedPendingRef = useRef(false);
  useEffect(() => {
    const p = pendingNavRef.current;
    if (!p) return;
    if (colsData && colsData !== p.prevDataId) {
      // Restore the scroll position from when the user clicked. As the columns
      // land, the auto-scroll snap and the draw-time clamp would otherwise pull
      // the finder back to the left edge — keep it where the loader was so the
      // rows just fill in place. The flag stops the auto-scroll effect (below)
      // from overriding this restore on the same commit.
      if (typeof p.scrollX === "number") st.current.scrollX = p.scrollX;
      pendingNavRef.current = null;
      justResolvedPendingRef.current = true;
      scheduleDraw();
    }
  }, [colsData, scheduleDraw]);

  // Auto-scroll to reveal a newly-added column — but not when it's the
  // resolution of a pending nav (showPendingColumn owns that scroll), so the
  // view stays put as the loader skeleton fills in with real rows.
  const prevColCount = useRef(columns.length);
  useEffect(() => {
    const wasPendingResolution = justResolvedPendingRef.current;
    justResolvedPendingRef.current = false;
    if (!wasPendingResolution && columns.length > prevColCount.current) {
      const canvas = canvasRef.current;
      if (canvas && canvas.clientWidth > 0) {
        const totalW = getTotalW(columns.length, st.current);
        const visW = canvas.clientWidth;
        st.current.scrollX = Math.max(0, totalW - visW);
      }
    }
    prevColCount.current = columns.length;
    scheduleDraw();
  }, [columns, scheduleDraw]);

  // ── Hit testing ────────────────────────────────────────────────

  const hitTest = useCallback((ev) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { col: -1, row: -1 };
    const mx = ev.clientX - r.left + st.current.scrollX;
    const my = ev.clientY - r.top;
    const pending = pendingNavRef.current;
    const hitColumns = pending
      ? columns.slice(0, Math.max(0, Math.min(pending.insertIndex ?? columns.length, columns.length)))
      : columns;
    let ci = -1;
    let cx = 0;
    for (let i = 0; i < hitColumns.length; i++) {
      const cw = getColW(i, st.current);
      if (mx >= cx && mx < cx + cw) { ci = i; break; }
      cx += cw;
    }
    if (ci < 0 || ci >= hitColumns.length || my < HDR_H) return { col: ci, row: -1 };
    const col = hitColumns[ci];
    const itemH = col.type === "picker" ? PICKER_H : ENTRY_H;
    const sy = st.current.colScrollY[ci] || 0;
    const ri = Math.floor((my - HDR_H + sy) / itemH);
    const items = col.type === "picker" ? col.items : col.entries;
    if (ri < 0 || ri >= items.length) return { col: ci, row: -1 };
    return { col: ci, row: ri };
  }, [columns]);

  // ── Click ──────────────────────────────────────────────────────

  const handleClick = useCallback((ev) => {
    if (didResizeRef.current) { didResizeRef.current = false; return; }
    if (didMinimapRef.current) { didMinimapRef.current = false; return; }
    if (didPanRef.current) { didPanRef.current = false; return; }
    // Check arrow button clicks first
    const r = canvasRef.current?.getBoundingClientRect();
    if (r) {
      const lx = ev.clientX - r.left;
      const maxSX = Math.max(0, getTotalW(columns.length, st.current) - r.width);
      if (st.current.scrollX > 0 && lx < 30) {
        st.current.scrollX = Math.max(0, st.current.scrollX - DEFAULT_COL_W);
        scheduleDraw();
        return;
      }
      if (st.current.scrollX < maxSX && lx > r.width - 30) {
        st.current.scrollX = Math.min(maxSX, st.current.scrollX + DEFAULT_COL_W);
        scheduleDraw();
        return;
      }
    }

    const { col: ci, row: ri } = hitTest(ev);
    if (ci < 0) return;

    // Hidden flow-bridge column (flow links, or "no flow available"). It's not
    // a real picker, so clicking it pops out of the downstream selection.
    if (hiddenFlowColsRef.current?.has(ci)) {
      const depth = columns[ci]?.depth ?? 0;
      startTransition(() => {
        if (depth < scopePath.length) {
          // A row at this depth is committed to the scope (e.g. a chosen
          // cluster). Pop just that selection but KEEP its dimension as the
          // trail, so the list + flow links stay — "unselect the right-hand
          // row" without falling back to the dimension picker (which would
          // force a re-pick and reload the flow lines).
          setTrailDim(scopePath[depth]?.dim ?? null);
          onChange(scopePath.slice(0, depth));
        } else {
          // The flow column IS the trailing picker — nothing committed at this
          // depth, so drop the trail back to the dimension picker.
          setTrailDim(null);
        }
      });
      return;
    }

    // Header click — navigate back to this column's parent scope
    if (ri < 0) {
      const col = columns[ci];
      if (col && col.depth > 0 && col.depth <= scopePath.length) {
        onChange(scopePath.slice(0, col.depth));
      }
      return;
    }
    const col = columns[ci];
    const showPendingColumn = (insertIndex, label = "", selectedId = null) => {
      const clampedIndex = Math.max(0, Math.min(insertIndex, columns.length));
      // selectedId: the dim the user just clicked in the source picker. The
      // glimmer targets it by id because the columns drawn during the load are
      // still stale (their item.selected reflects the prior selection).
      pendingNavRef.current = { prevDataId: colsData, insertIndex: clampedIndex, label, selectedId };
      const canvas = canvasRef.current;
      if (canvas) {
        const skW = clampedIndex < columns.length ? getColW(clampedIndex, st.current) : DEFAULT_COL_W;
        const skX = getColX(clampedIndex, st.current);
        const skRight = skX + skW;
        const viewLeft = st.current.scrollX;
        const viewRight = viewLeft + canvas.clientWidth;
        const pendingTotalW = getTotalW(clampedIndex, st.current) + skW;
        const maxScrollX = Math.max(0, pendingTotalW - canvas.clientWidth);
        if (skX < viewLeft) {
          st.current.scrollX = Math.max(0, Math.min(maxScrollX, skX));
        } else if (skRight > viewRight) {
          st.current.scrollX = Math.max(0, Math.min(maxScrollX, skRight - canvas.clientWidth));
        }
      }
      // Remember the scroll we settled on so the resolution can restore it —
      // as the skeleton fills in with real rows, the finder must not jump back
      // to the left edge.
      pendingNavRef.current.scrollX = st.current.scrollX;
      draw();
    };
    if (col.type === "picker") {
      const item = col.items[ri];
      if (col.depth < scopePath.length) {
        // Confirmed picker — switch dimension. Scope truncation and trail-dim
        // update both need to be transitions so React can render the worker
        // response at interruptible priority.
        if (item.id !== scopePath[col.depth].dim) {
          showPendingColumn(ci + 1, item.label || item.id, item.id);
          startTransition(() => {
            onChange(scopePath.slice(0, col.depth));
            setTrailDim(item.id);
          });
        }
      } else {
        if (item.id !== trailDim) {
          showPendingColumn(ci + 1, item.label || item.id, item.id);
          startTransition(() => setTrailDim(item.id));
        }
      }
    } else {
      const item = col.entries[ri];
      showPendingColumn(ci + 1, "", item.id);

      const additive = (ev.metaKey || ev.ctrlKey) && canMultiSelect(col.dimKey);
      // Existing ids selected at this depth (only meaningful when the
      // current scope step at col.depth matches the column's dim).
      const existingStep = scopePath[col.depth];
      const sameDim = existingStep && existingStep.dim === col.dimKey;
      const existingIds = sameDim ? stepIds(existingStep) : [];

      let nextIds;
      if (additive) {
        nextIds = existingIds.includes(item.id)
          ? existingIds.filter((id) => id !== item.id)
          : [...existingIds, item.id];
      } else {
        nextIds = [item.id];
      }

      startTransition(() => {
        setTrailDim(null);
        if (nextIds.length === 0) {
          // Cmd-clicked the last id off — drop the step.
          onChange(scopePath.slice(0, col.depth));
        } else {
          onChange([
            ...scopePath.slice(0, col.depth),
            { dim: col.dimKey, ids: nextIds, id: nextIds[0] },
          ]);
        }
      });
    }
  }, [columns, scopePath, onChange, hitTest, scheduleDraw, colsData, draw, trailDim]);

  // ── Hover ──────────────────────────────────────────────────────

  // Overflow tooltip for entries rows whose label gets truncated by
  // the canvas drawer (lines ~684–688 below). Mirrors the same maxLW
  // calc + font so detection matches whatever ctx.fillText emits, and
  // renders a DOM overlay anchored beneath the hovered row.
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const measureCtxRef = useRef(null);
  const getMeasureCtx = useCallback(() => {
    if (!measureCtxRef.current) {
      measureCtxRef.current = document.createElement("canvas").getContext("2d");
    }
    return measureCtxRef.current;
  }, []);

  const computeHoverTooltip = useCallback((col, row) => {
    if (col < 0 || row < 0) return null;
    const c = columns[col];
    if (!c || c.type !== "entries") return null;
    const item = (c.entries || [])[row];
    const text = item?.label ? String(item.label) : "";
    if (!text) return null;
    const colW = getColW(col, st.current);
    const maxLW = colW - PX - 50 - MINIMAP_W;
    const mctx = getMeasureCtx();
    mctx.font = "500 12px Inter,system-ui,sans-serif";
    if (mctx.measureText(text).width <= maxLW) return null;
    const canvas = canvasRef.current;
    const r = canvas?.getBoundingClientRect();
    if (!r) return null;
    const cx = getColX(col, st.current) - st.current.scrollX;
    const sy = st.current.colScrollY[col] || 0;
    const iy = HDR_H + row * ENTRY_H - sy;
    return {
      left: r.left + cx + PX,
      top: r.top + iy + ENTRY_H + 4,
      text,
    };
  }, [columns, getMeasureCtx]);

  const handleMouseMove = useCallback((ev) => {
    const { col, row } = hitTest(ev);
    if (col !== st.current.hoverCol || row !== st.current.hoverRow) {
      st.current.hoverCol = col;
      st.current.hoverRow = row;
      scheduleDraw();
      setHoverTooltip(computeHoverTooltip(col, row));
    }
  }, [hitTest, scheduleDraw, computeHoverTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (st.current.hoverCol !== -1) {
      st.current.hoverCol = -1;
      st.current.hoverRow = -1;
      scheduleDraw();
    }
    setHoverTooltip(null);
  }, [scheduleDraw]);

  // ── Scroll (wheel) — non-passive for preventDefault ────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (ev) => {
      const { col: ci } = hitTest(ev);
      const totalW = getTotalW(columns.length, st.current);
      const visW = canvas.clientWidth;

      // Horizontal scroll (shift+wheel or trackpad deltaX)
      if (Math.abs(ev.deltaX) > Math.abs(ev.deltaY) || ev.shiftKey) {
        const dx = ev.shiftKey ? ev.deltaY : ev.deltaX;
        st.current.scrollX = Math.max(0, Math.min(totalW - visW, st.current.scrollX + dx));
        ev.preventDefault();
        scheduleDraw();
        return;
      }

      // Vertical scroll within the hovered column
      if (ci >= 0 && ci < columns.length) {
        const col = columns[ci];
        const items = col.type === "picker" ? col.items : col.entries;
        const itemH = col.type === "picker" ? PICKER_H : ENTRY_H;
        const maxS = Math.max(0, items.length * itemH - (canvas.clientHeight - HDR_H));
        if (maxS > 0) {
          const cur = st.current.colScrollY[ci] || 0;
          const next = Math.max(0, Math.min(maxS, cur + ev.deltaY));
          ev.preventDefault();
          // Only react if the scroll position actually moved. Trackpad
          // momentum and tiny rest-deltas at the top/bottom boundary
          // emit wheel events whose effective delta clamps to zero —
          // bumping scrollTick on those would churn the flow loader on
          // every mouse twitch.
          if (next !== cur) {
            st.current.colScrollY[ci] = next;
            scheduleDraw();
            bumpScrollTick();
          }
        }
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [columns, hitTest, scheduleDraw]);

  // ── Column resize ──────────────────────────────────────────────

  function findResizeBorder(ev) {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return -1;
    const mx = ev.clientX - r.left + st.current.scrollX;
    const pending = pendingNavRef.current;
    const resizeColumns = pending
      ? columns.slice(0, Math.max(0, Math.min(pending.insertIndex ?? columns.length, columns.length)))
      : columns;
    for (let i = 0; i < resizeColumns.length; i++) {
      const rightEdge = getColX(i, st.current) + getColW(i, st.current);
      // Narrowed to the outer edge reserve so the minimap can own the
      // remaining strip inside of it.
      if (Math.abs(mx - rightEdge) <= MINIMAP_EDGE_RESERVE) return i;
    }
    return -1;
  }

  // Hit-test the minimap strip for an entries column.
  function findMinimapHit(ev) {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return null;
    const mx = ev.clientX - r.left + st.current.scrollX;
    const cy = ev.clientY - r.top;
    if (cy < HDR_H) return null;
    const pending = pendingNavRef.current;
    const minimapColumns = pending
      ? columns.slice(0, Math.max(0, Math.min(pending.insertIndex ?? columns.length, columns.length)))
      : columns;
    for (let i = 0; i < minimapColumns.length; i++) {
      const col = minimapColumns[i];
      if (col.type !== "entries") continue;
      const rightEdge = getColX(i, st.current) + getColW(i, st.current);
      const mmLeft = rightEdge - MINIMAP_W;
      const mmRight = rightEdge - MINIMAP_EDGE_RESERVE;
      if (mx >= mmLeft && mx < mmRight) {
        return { ci: i, mmY: HDR_H, mmH: r.height - HDR_H, cy };
      }
    }
    return null;
  }

  function minimapScrollTo(hit) {
    const col = columns[hit.ci];
    const items = col.entries;
    const itemH = ENTRY_H;
    const totalH = items.length * itemH;
    const maxSy = Math.max(0, totalH - hit.mmH);
    if (maxSy === 0) return;
    const fraction = Math.max(0, Math.min(1, (hit.cy - hit.mmY) / hit.mmH));
    st.current.colScrollY[hit.ci] = fraction * maxSy;
    scheduleDraw();
    bumpScrollTick();
  }

  const didResizeRef = useRef(false);
  const minimapDragRef = useRef(null); // { ci, mmY, mmH } while dragging
  const didMinimapRef = useRef(false);
  const panRef = useRef(null); // { startX, startScrollX } while drag-panning the strip
  const didPanRef = useRef(false); // true once a pan moved — suppresses the click

  const handlePointerDown = useCallback((ev) => {
    // Resize border first (it owns the outer reserve).
    const ci = findResizeBorder(ev);
    if (ci >= 0) {
      ev.preventDefault();
      didResizeRef.current = false;
      resizeRef.current = { ci, startX: ev.clientX, startW: getColW(ci, st.current) };
      canvasRef.current?.setPointerCapture(ev.pointerId);
      return;
    }
    // Minimap strip: start a drag-scroll for that column.
    const mmHit = findMinimapHit(ev);
    if (mmHit) {
      ev.preventDefault();
      didMinimapRef.current = true;
      minimapDragRef.current = { ci: mmHit.ci, mmY: mmHit.mmY, mmH: mmHit.mmH };
      minimapScrollTo(mmHit);
      canvasRef.current?.setPointerCapture(ev.pointerId);
      return;
    }
    // Otherwise arm a horizontal pan of the whole strip — but only when there's
    // something to pan. It becomes a pan once the pointer moves (see
    // handlePointerMove); a press with no movement stays a click (select/drill).
    const canvas = canvasRef.current;
    const totalW = getTotalW(columns.length, st.current);
    if (canvas && totalW > canvas.clientWidth + 1) {
      didPanRef.current = false;
      panRef.current = { startX: ev.clientX, startScrollX: st.current.scrollX };
      canvas.setPointerCapture(ev.pointerId);
    }
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePointerMove = useCallback((ev) => {
    const drag = resizeRef.current;
    if (drag) {
      const delta = ev.clientX - drag.startX;
      st.current.colWidths[drag.ci] = Math.max(MIN_COL_W, drag.startW + delta);
      if (Math.abs(delta) > 2) didResizeRef.current = true;
      scheduleDraw();
      return;
    }
    if (minimapDragRef.current) {
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = "move";
      const r = canvas?.getBoundingClientRect();
      if (!r) return;
      const cy = ev.clientY - r.top;
      minimapScrollTo({ ...minimapDragRef.current, cy });
      return;
    }
    if (panRef.current) {
      const canvas = canvasRef.current;
      const dx = ev.clientX - panRef.current.startX;
      // Only start panning past a small threshold so a steady click still
      // selects/drills; once panning, keep tracking with the cursor.
      if (didPanRef.current || Math.abs(dx) > 4) {
        didPanRef.current = true;
        const totalW = getTotalW(columns.length, st.current);
        const visW = canvas?.clientWidth ?? 0;
        const maxScrollX = Math.max(0, totalW - visW);
        st.current.scrollX = Math.max(0, Math.min(maxScrollX, panRef.current.startScrollX - dx));
        if (canvas) canvas.style.cursor = "grabbing";
        setHoverTooltip(null);
        scheduleDraw();
      }
      return;
    }
    // Update cursor for resize / minimap affordance
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (findResizeBorder(ev) >= 0) {
      canvas.style.cursor = "col-resize";
    } else if (findMinimapHit(ev)) {
      canvas.style.cursor = "move";
    } else {
      canvas.style.cursor = "pointer";
    }
    // Also run hover
    handleMouseMove(ev);
  }, [columns, scheduleDraw, handleMouseMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePointerUp = useCallback((ev) => {
    if (resizeRef.current) {
      resizeRef.current = null;
      canvasRef.current?.releasePointerCapture(ev.pointerId);
    }
    if (minimapDragRef.current) {
      minimapDragRef.current = null;
      canvasRef.current?.releasePointerCapture(ev.pointerId);
    }
    if (panRef.current) {
      panRef.current = null;
      canvasRef.current?.releasePointerCapture(ev.pointerId);
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = "pointer";
    }
  }, []);

  // Keyboard navigation. The canvas takes focus on click (tabIndex=0)
  // and intercepts arrow / Page / Home / End keys here. Vertical keys
  // act on whichever column the mouse is hovering, falling back to the
  // first entries column. Horizontal keys scroll the column strip.
  const handleKeyDown = useCallback((ev) => {
    const canvas = canvasRef.current;
    if (!canvas || !columns.length) return;
    const ci = st.current.hoverCol >= 0
      ? st.current.hoverCol
      : columns.findIndex((c) => c.type === "entries");
    if (ci < 0) return;
    const col = columns[ci];
    const items = col.type === "picker" ? col.items : col.entries;
    const itemH = col.type === "picker" ? PICKER_H : ENTRY_H;
    const contentH = canvas.clientHeight - HDR_H;
    const maxSy = Math.max(0, items.length * itemH - contentH);

    let handled = true;
    let scrolled = false;
    switch (ev.key) {
      case "ArrowDown": {
        if (maxSy > 0) {
          const cur = st.current.colScrollY[ci] || 0;
          const next = Math.min(maxSy, cur + itemH);
          if (next !== cur) { st.current.colScrollY[ci] = next; scrolled = true; }
        }
        break;
      }
      case "ArrowUp": {
        if (maxSy > 0) {
          const cur = st.current.colScrollY[ci] || 0;
          const next = Math.max(0, cur - itemH);
          if (next !== cur) { st.current.colScrollY[ci] = next; scrolled = true; }
        }
        break;
      }
      case "PageDown": {
        if (maxSy > 0) {
          const cur = st.current.colScrollY[ci] || 0;
          const next = Math.min(maxSy, cur + contentH);
          if (next !== cur) { st.current.colScrollY[ci] = next; scrolled = true; }
        }
        break;
      }
      case "PageUp": {
        if (maxSy > 0) {
          const cur = st.current.colScrollY[ci] || 0;
          const next = Math.max(0, cur - contentH);
          if (next !== cur) { st.current.colScrollY[ci] = next; scrolled = true; }
        }
        break;
      }
      case "Home": {
        if ((st.current.colScrollY[ci] || 0) !== 0) {
          st.current.colScrollY[ci] = 0;
          scrolled = true;
        }
        break;
      }
      case "End": {
        if ((st.current.colScrollY[ci] || 0) !== maxSy && maxSy > 0) {
          st.current.colScrollY[ci] = maxSy;
          scrolled = true;
        }
        break;
      }
      case "ArrowLeft":
      case "ArrowRight": {
        const dir = ev.key === "ArrowLeft" ? -1 : 1;
        const maxSX = Math.max(0, getTotalW(columns.length, st.current) - canvas.clientWidth);
        const cur = st.current.scrollX;
        const next = Math.max(0, Math.min(maxSX, cur + dir * DEFAULT_COL_W));
        if (next !== cur) { st.current.scrollX = next; scrolled = true; }
        break;
      }
      default:
        handled = false;
    }
    if (handled) {
      ev.preventDefault();
      if (scrolled) {
        scheduleDraw();
        bumpScrollTick();
      }
    }
  }, [columns, scheduleDraw, bumpScrollTick]);

  // ── Render ─────────────────────────────────────────────────────

  return (
    <>
      {/* overflow-hidden is load-bearing: mid-resize the canvas is pinned
          to its last-drawn pixel size (see the resize effect), so a
          shrinking container must clip it rather than let it paint over
          the divider / detail panel. */}
      <div className="relative w-full h-full overflow-hidden">
        <canvas
          ref={canvasRef}
          className="block w-full h-full cursor-pointer outline-none"
          style={{
            contain: "strict",
          }}
          tabIndex={0}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onMouseLeave={handleMouseLeave}
          onKeyDown={handleKeyDown}
        />
        <div
          ref={headerOverlayRef}
          className="pointer-events-none absolute left-0 right-0 top-0 overflow-hidden"
          style={{ height: HDR_H }}
          aria-hidden={false}
        >
          {columns.map((col, ci) => {
            if (col.type !== "entries" || !col.dimKey) return null;
            const depth = col.depth ?? 0;
            const parentScopePath = scopePath.slice(0, depth);
            return (
              <div
                key={`${depth}:${col.dimKey}`}
                data-col-idx={ci}
                className="absolute left-0 top-0"
                style={{ height: HDR_H, willChange: "transform" }}
              >
                <ColumnHeaderToolbar
                  depth={depth}
                  dimKey={col.dimKey}
                  parentScopePath={parentScopePath}
                  cacheRef={cacheRef}
                />
              </div>
            );
          })}
        </div>
        {colsError ? (
          <div
            role="status"
            className="absolute inset-0 grid place-items-center bg-[color:var(--bg)]/95 px-[24px] text-center text-[12px] leading-[1.45] text-[color:var(--muted)]"
          >
            {analyticsErrorCopy(colsError, caps).message}
          </div>
        ) : null}
      </div>
      {hoverTooltip ? createPortal(
        <div
          role="tooltip"
          className={`pointer-events-none fixed ${TOOLTIP_LAYER} whitespace-normal break-words border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--ink)] shadow-lg`}
          style={{
            top: `${hoverTooltip.top}px`,
            left: `${hoverTooltip.left}px`,
            maxWidth: "420px",
          }}
        >
          {hoverTooltip.text}
        </div>,
        document.body,
      ) : null}
    </>
  );
});
